<template>
  <div class="ex-checkpoint-inspection-instructions">
    <div
      class="ex-checkpoint-inspection-instructions__content"
      :class="{
        'ex-checkpoint-inspection-instructions__content--with-video':
          showProposedVideoNoInstructions,
      }"
    >
      <template v-if="showActiveVideoMenuItem">
        <div class="ex-checkpoint-inspection-instructions__proposed-header">
          <div class="ex-checkpoint-inspection-instructions__proposed-label">
            <CheckpointToolsMenuItem
              variant="Active Learning, Testing and Inspection"
              :video="true"
              :selected="true"
            />
          </div>
          <div class="ex-checkpoint-inspection-instructions__actions">
            <StandardButton type="magenta" variant="primary" size="small" label="Propose Changes" />
            <StandardButton
              type="red"
              variant="outline"
              size="small"
              label="Remove"
              :disabled="isRemoveActiveLoading"
              @click="handleRemoveActive"
            />
          </div>
        </div>
        <div class="ex-checkpoint-inspection-instructions__separator" />
        <div class="ex-checkpoint-inspection-instructions__microlearning-view-container">
          <AsAMasterCheckpointInspectionKitMenu
            :value="0"
            :showRightActions="true"
            @onChange="() => {}"
            @three-dots-click="handleActiveThreeDotsClick"
          />
          <div class="ex-checkpoint-inspection-instructions__microlearning-view">
            <MicrolearningViewEdit
              :video="activeParsedInstructions.video_url || activeVideoUrl"
              :error="activeParsedInstructions.error || null"
              :summary="activeParsedInstructions.summary || null"
              :steps="activeParsedInstructions.steps || []"
              :editable="false"
              @step-click="handleActiveStepClick"
            />
          </div>
        </div>
      </template>
      <template v-else-if="showProposedVideoMenuItem">
        <!-- Legacy: double header. MDP-452: only "Source Video" bar + player (no Proposed Learning strip). -->
        <template v-if="!ideaMDP452">
          <div class="ex-checkpoint-inspection-instructions__proposed-header">
            <div class="ex-checkpoint-inspection-instructions__proposed-label">
              <CheckpointToolsMenuItem
                variant="Proposed Learning, Testing and Inspection"
                :video="true"
                :selected="true"
              />
            </div>
            <div class="ex-checkpoint-inspection-instructions__actions">
              <StandardButton
                type="magenta"
                variant="primary"
                size="small"
                label="Publish"
                :disabled="!hasProposedInstructions"
                @click="handlePublishClick"
              />
              <StandardButton
                type="red"
                variant="outline"
                size="small"
                label="Discard"
                :disabled="isDiscardLoading"
                @click="handleDiscard"
              />
            </div>
          </div>
          <div class="ex-checkpoint-inspection-instructions__separator" />
        </template>

        <template v-if="showProposedVideoWithInstructions">
          <div class="ex-checkpoint-inspection-instructions__microlearning-view-container">
            <AsAMasterCheckpointInspectionKitMenu
              :value="0"
              :showRightActions="true"
              @onChange="() => {}"
            />
            <div class="ex-checkpoint-inspection-instructions__microlearning-view">
              <MicrolearningViewEdit
                :video="proposedParsedInstructions.video_url || proposedVideoUrl"
                :error="proposedParsedInstructions.error || null"
                :summary="proposedParsedInstructions.summary || null"
                :steps="proposedParsedInstructions.steps || []"
                :editable="true"
              />
            </div>
          </div>
        </template>

        <template v-else-if="showProposedVideoNoInstructions">
          <div
            v-if="ideaMDP452 && showInlineFromVideoIq"
            class="ex-checkpoint-inspection-instructions__inline-mini-iq"
          >
            <div
              v-if="showInlineFromVideoFailReview"
              class="ex-checkpoint-inspection-instructions__inline-fail-panel"
            >
              <div class="ex-checkpoint-inspection-instructions__inline-fail-actions">
                <StandardButton
                  type="magenta"
                  variant="primary"
                  size="small"
                  label="Replace"
                  :disabled="isDiscardLoading"
                  @click="handleInlineFailReplace"
                />
                <StandardButton
                  type="red"
                  variant="primary"
                  size="small"
                  label="Delete"
                  :disabled="isDiscardLoading"
                  @click="openDeleteMicrolearningVideoDialog"
                />
                <AddORAskIQButton
                  class="ex-checkpoint-inspection-instructions__inline-fail-regenerate"
                  :show-add-button="false"
                  :show-a-i-button="true"
                  label="Regenerate Inspection"
                  emp-label="Regenerate Inspection"
                  @addAI="handleSuggestInstructions"
                />
              </div>
              <MicrolearningViewEdit
                :video="
                  inlineFromVideoSuggestedInstructions.video_url ||
                  proposedParsedInstructions.video_url ||
                  proposedVideoUrl ||
                  ''
                "
                :error="inlineFromVideoSuggestedInstructions.error || null"
                :summary="inlineFromVideoSuggestedInstructions.summary || null"
                :steps="inlineFromVideoSuggestedInstructions.steps || []"
                :editable="false"
              />
            </div>
            <div
              v-else-if="showInlineFromVideoPassToolbar"
              class="ex-checkpoint-inspection-instructions__inline-pass-toolbar"
              role="region"
              aria-label="Generated content"
            >
              <div class="ex-inline-pass-toolbar__main">
                <div class="ex-inline-pass-toolbar__horizontal">
                  <HorizontalMenu
                    :key="passToolbarHorizontalMenuKey"
                    :items="passToolbarHorizontalItems"
                    :value="passToolbarTabIndexNum"
                    size="small"
                    :bottom-line="false"
                    :hide-lines="true"
                    @onChange="handlePassToolbarHorizontalChange"
                  />
                </div>
                <div class="ex-inline-pass-toolbar__icons">
                  <CheckpointMicrolearningIcons
                    class="ex-inline-pass-toolbar__microlearning-icons"
                    type="Video with TOC"
                    :quiz="inlinePassToolbarIconsQuiz"
                    :inspection="inlinePassToolbarIconsInspection"
                  />
                </div>
              </div>
              <div class="ex-inline-pass-toolbar__actions">
                <StandardButton
                  type="red"
                  variant="outline"
                  size="small"
                  label="Remove Microlearning"
                  :disabled="isDiscardLoading"
                  @click="openRemoveMicrolearningDialog('video')"
                />
              </div>
              <div class="ex-inline-pass-toolbar__body ex-inline-pass-toolbar__body--full-row">
                <MicrolearningViewEdit
                  v-if="passToolbarTabIndexNum === 0"
                  :key="'pass-toolbar-microlearning'"
                  :video="inlinePassChapterVideoUrl"
                  :error="null"
                  :summary="inlinePassChapterSummary"
                  :steps="inlinePassChapterSteps"
                  :showSteps="false"
                  :editable="false"
                />
                <div
                  v-else-if="passToolbarTabIndexNum === 1"
                  :key="'pass-toolbar-quiz'"
                  class="ex-inline-pass-toolbar__quiz"
                >
                  <QuizEditor
                    :quiz="proposedArticleQuizPayload"
                    :questions="
                      proposedArticleQuizQuestions.length ? proposedArticleQuizQuestions : null
                    "
                    :quiz-mode="inlinePassQuizMode"
                    @add-question="handleAddQuestion"
                    @delete-question="handleDeleteQuestion"
                    @update-question="handleUpdateQuestion"
                    @reorder-questions="handleReorderQuestions"
                    @quiz-bulk-action="handleQuizBulkMenuAction"
                    @update:autopass="handleInlinePassQuizAutopassChange"
                  />
                </div>
                <div
                  v-else-if="passToolbarTabIndexNum === 2 && !inlinePassQuizAutopass"
                  :key="'pass-toolbar-protocol'"
                  class="ex-inline-pass-toolbar__protocol"
                >
                  <InspectionEditor
                    :protocol="proposedArticleInspectionProtocolParsed"
                    :steps="proposedArticleInspectionSteps"
                    :confirm-every-step="proposedArticleInspectionConfirmEveryStep"
                    @add-step="handleAddInspectionStep"
                    @update-step="handleUpdateInspectionStep"
                    @delete-step="handleDeleteInspectionStep"
                    @update:confirmEveryStep="handleProposedArticleInspectionConfirmEveryStepChange"
                    @delete-protocol-requested="handleDeleteInspectionProtocolRequested"
                    @suggest="$emit('iq-suggest-click')"
                    @timestamp-click="handleTimestampClick"
                  />
                </div>
              </div>
            </div>
            <MiniIQOverlay
              v-else
              :user-prompt="inlineFromVideoIqUserPrompt"
              :video-url="proposedVideoUrl || ''"
              :checkpoint-prefix="checkpointPrefix"
              :checkpoint-criteria="checkpointCriteria"
              :responsibility-name="responsibilityName"
              :responsibility-purpose="responsibilityPurpose"
              :video-to-components-selection="inlineVideoToComponentsSelection"
              @open-review="handleInlineFromVideoIqOpenReview"
              @dismiss="handleInlineFromVideoIqDismiss"
            />
          </div>
          <template v-else>
            <MicrolearningVideoWithGenerateInspectionBar
              v-if="!isProposedDraftStreamLoading && !postUploadVideoRevealPending"
              class="ex-checkpoint-inspection-instructions__video-bar"
              :section-title="ideaMDP452 ? 'Source Video' : 'Microlearning Video'"
              :generate-button-label="
                ideaMDP452
                  ? 'Generate Microlearning and Inspection Protocol'
                  : 'Generate Inspection'
              "
              @suggest-instructions="handleSuggestInstructions"
            />
            <div
              class="ex-checkpoint-inspection-instructions__video-wrap"
              :class="{
                'ex-checkpoint-inspection-instructions__video-wrap--loading':
                  isProposedDraftStreamLoading,
                'ex-checkpoint-inspection-instructions__video-wrap--mdp452-stream-compact':
                  ideaMDP452 && isProposedDraftStreamLoading,
              }"
            >
              <!-- Processing state -->
              <div
                v-if="isProposedDraftStreamLoading"
                class="ex-checkpoint-inspection-instructions__stream-loading ex-checkpoint-inspection-instructions__stream-loading--in-player"
                :class="{
                  'ex-checkpoint-inspection-instructions__stream-loading--mdp452-in-player-compact':
                    ideaMDP452,
                }"
              >
                <div
                  class="ex-checkpoint-inspection-instructions__video-processing-loader-wrap"
                  aria-hidden="true"
                >
                  <StreamProcessingLoaderSvg
                    class="ex-checkpoint-inspection-instructions__video-processing-loader"
                  />
                </div>
                <p class="ex-checkpoint-inspection-instructions__video-processing-text">
                  Video is being processed ...
                </p>
              </div>
              <div
                v-else-if="
                  proposedVideoUrl &&
                  isProposedVideoIframe &&
                  !pendingInspectionStreamReveal &&
                  streamManifestReady &&
                  !postUploadVideoRevealPending &&
                  !pendingPostProposeStreamReveal
                "
                v-html="proposedVideoUrl"
                class="ex-checkpoint-inspection-instructions__video-iframe"
              />
              <iframe
                v-else-if="
                  proposedVideoUrl &&
                  !pendingInspectionStreamReveal &&
                  streamManifestReady &&
                  !postUploadVideoRevealPending &&
                  !pendingPostProposeStreamReveal
                "
                :key="proposedVideoUrl"
                :src="proposedVideoUrl"
                class="ex-checkpoint-inspection-instructions__video"
                :class="{
                  'ex-checkpoint-inspection-instructions__video--hidden': isProposedVideoProcessing,
                }"
                allowfullscreen
                allow="
                  accelerometer;
                  autoplay;
                  clipboard-write;
                  encrypted-media;
                  gyroscope;
                  picture-in-picture;
                "
                frameborder="0"
              />
              <div v-else class="ex-checkpoint-inspection-instructions__video-placeholder">
                <p>No microlearning video available.</p>
              </div>
            </div>
          </template>
        </template>
      </template>
      <template v-else-if="showProposedArticleEditor">
        <div class="ex-checkpoint-inspection-instructions__article-microlearning">
          <CheckpointMicrolearningMenu
            type="Article"
            :article-html="proposedArticleHtmlForEditor"
            :article-body="proposedArticleBodyForEditor"
            :article-title-hint="checkpointCriteria"
            :article-editor-key="articleMenuEditorKey"
            :proposed-article="proposedArticleForMenu"
            :has-proposed-quiz="proposedArticleHasQuizForMenuIcons"
            :has-proposed-inspection="proposedArticleHasInspectionForMenuIcons"
            :proposed-quiz-is-autopass="proposedArticleQuizAutopass"
            :readonly="false"
            @update:article-html="handleProposedArticleHtmlUpdate"
            @tabChange="handleProposedArticleMenuTabChange"
            @remove-request="openRemoveMicrolearningDialog('article')"
            @article-save="handleProposedArticleSave"
            @article-discard="handleProposedArticleDiscard"
          />
          <div
            v-if="proposedArticleMenuTabIndex === 1"
            class="ex-checkpoint-inspection-instructions__article-quiz"
          >
            <QuizEditor
              :quiz="proposedArticleQuizPayload"
              :questions="proposedArticleQuizQuestions.length ? proposedArticleQuizQuestions : null"
              :quiz-mode="proposedArticleQuizMode"
              @add-question="handleAddQuestion"
              @delete-question="handleDeleteQuestion"
              @update-question="handleUpdateQuestion"
              @reorder-questions="handleReorderQuestions"
              @quiz-bulk-action="handleQuizBulkMenuAction"
              @update:autopass="handleInlinePassQuizAutopassChange"
            />
          </div>
          <div
            v-if="proposedArticleMenuTabIndex === 2 && !proposedArticleQuizAutopass"
            class="ex-checkpoint-inspection-instructions__article-inspection"
          >
            <InspectionEditor
              :protocol="proposedArticleInspectionProtocolParsed"
              :steps="proposedArticleInspectionSteps.length ? proposedArticleInspectionSteps : null"
              :confirm-every-step="proposedArticleInspectionConfirmEveryStep"
              @add-step="handleAddInspectionStep"
              @update-step="handleUpdateInspectionStep"
              @delete-step="handleDeleteInspectionStep"
              @update:confirmEveryStep="handleProposedArticleInspectionConfirmEveryStepChange"
              @delete-protocol-requested="handleDeleteInspectionProtocolRequested"
              @suggest="$emit('iq-suggest-click')"
              @timestamp-click="handleTimestampClick"
            />
          </div>
        </div>
      </template>
      <div
        v-else-if="pendingInspectionStreamReveal"
        class="ex-checkpoint-inspection-instructions__stream-loading ex-checkpoint-inspection-instructions__stream-loading--banner"
        :class="{
          'ex-checkpoint-inspection-instructions__stream-loading--banner-mdp452-compact':
            ideaMDP452,
        }"
      >
        <div
          class="ex-checkpoint-inspection-instructions__video-processing-loader-wrap"
          aria-hidden="true"
        >
          <StreamProcessingLoaderSvg
            class="ex-checkpoint-inspection-instructions__video-processing-loader"
          />
        </div>
        <p class="ex-checkpoint-inspection-instructions__video-processing-text">
          Video is being processed ...
        </p>
      </div>
      <ProposeInspectionToolsBanner
        v-else
        @microlearning-upload-click="handleMicrolearningUploadClick"
        @task-employee-click="handleTaskEmployeeClick"
        @propose-click="$emit('propose-click')"
        @iq-suggest-click="$emit('iq-suggest-click')"
      />
    </div>

    <UploadMicroLearningPopup
      v-model="showUploadDialog"
      :responsibilityId="responsibilityId"
      :checkpointId="checkpointId"
      :closeAfterUpload="false"
      :uploadUrl="pendingUploadUrl"
      :videoId="pendingVideoId"
      @uploaded="handleVideoUploaded"
      @video-url-ready="handleVideoUrlReady"
      @refresh-checkpoint="emit('refresh-checkpoint')"
    />

    <TaskEmployeeToUploadVideoDialog
      :dialog="showTaskEmployeeDialog"
      :checkpoint-description="checkpointDescriptionForTaskDialog"
      submit-label="Assign Task"
      :wizard="false"
      @back="showTaskEmployeeDialog = false"
      @onAction="handleTaskEmployeeAssign"
    />
    <PublishVideoInspectionKitDialog
      :show="showPublishDialog"
      @publish="handlePublishConfirm"
      @close="showPublishDialog = false"
    />
    <GenerateVideoMicrolearningDialog
      :value="showGenerateVideoMicrolearningDialog"
      :initial-video-type="generateVideoMicrolearningDialogInitialVideoType"
      :initial-testing-type="generateVideoMicrolearningDialogInitialTestingType"
      @input="onGenerateVideoMicrolearningDialogInput"
      @generate="handleGenerateVideoMicrolearningDialogSubmit"
    />
    <DeleteQuizDialog
      :dialog="showDeleteEntireQuizDialog"
      :submitting="deleteEntireQuizSubmitting"
      @close="showDeleteEntireQuizDialog = false"
      @confirm="onConfirmDeleteEntireQuizDialog"
    />
    <RemoveMicrolearningDialog
      :value="showRemoveMicrolearningDialog"
      @input="showRemoveMicrolearningDialog = $event"
      @onAction="handleRemoveMicrolearningDialogConfirm"
    />
    <DeleteMicrolearningVideoDialog
      :value="showDeleteMicrolearningVideoDialog"
      @input="showDeleteMicrolearningVideoDialog = $event"
      @onAction="handleDeleteMicrolearningVideoDialogConfirm"
    />
    <v-dialog
      v-model="showTimestampVideoModal"
      max-width="800px"
      @click:outside="closeTimestampVideoModal"
    >
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span>Microlearning Video</span>
          <v-btn
            icon
            small
            aria-label="Close microlearning video"
            @click="closeTimestampVideoModal"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-4">
          <div class="timestamp-video-modal__video-container">
            <iframe
              v-if="timestampVideoUrl"
              :src="timestampVideoUrl"
              class="timestamp-video-modal__video-frame"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              allowfullscreen
            ></iframe>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import CheckpointMicrolearningMenu from "@components/CheckpointMicrolearningMenu/CheckpointMicrolearningMenu.vue";
import ProposeInspectionToolsBanner from "@components/ProposeInspectionToolsBanner/ProposeInspectionToolsBanner.vue";
import UploadMicroLearningPopup from "@components/micro-learning/UploadMicroLearningPopup.vue";
import TaskEmployeeToUploadVideoDialog from "@components/TaskEmployeeToUploadVideoDialog/TaskEmployeeToUploadVideoDialog.vue";
import CheckpointToolsMenuItem from "@components/CheckpointToolsMenuItem/CheckpointToolsMenuItem.vue";
import MicrolearningVideoWithGenerateInspectionBar from "@components/MicrolearningVideoWithGenerateInspectionBar/MicrolearningVideoWithGenerateInspectionBar.vue";
import MicrolearningViewEdit from "@components/MicrolearningViewEdit/MicrolearningViewEdit.vue";
import QuizEditor from "@components/QuizEditor/QuizEditor.vue";
import {
  MIN_QUIZ_ANSWERS,
  MAX_QUIZ_ANSWERS,
  QUIZ_QUESTION_INVALID_MESSAGE,
} from "@components/constants/quizLimits";
import InspectionEditor from "@components/InspectionEditor/InspectionEditor.vue";
import AsAMasterCheckpointInspectionKitMenu from "@components/AsAMasterCheckpointInspectionKitMenu/AsAMasterCheckpointInspectionKitMenu.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import AddORAskIQButton from "@components/AddORAskIQButton/AddORAskIQButton.vue";
import PublishVideoInspectionKitDialog from "@components/PublishVideoInspectionKitDialog/PublishVideoInspectionKitDialog.vue";
import GenerateVideoMicrolearningDialog from "@components/GenerateVideoMicrolearningDialog/GenerateVideoMicrolearningDialog.vue";
import DeleteQuizDialog from "@components/DeleteQuizDialog/DeleteQuizDialog.vue";
import RemoveMicrolearningDialog from "@components/RemoveMicrolearningDialog/RemoveMicrolearningDialog.vue";
import DeleteMicrolearningVideoDialog from "@components/DeleteMicrolearningVideoDialog/DeleteMicrolearningVideoDialog.vue";
import StreamProcessingLoaderSvg from "@components/StreamProcessingLoaderSvg/StreamProcessingLoaderSvg.vue";
import MiniIQOverlay from "@components/MiniIQOverlay/MiniIQOverlay.vue";
import HorizontalMenu from "@components/HorizontalMenu/HorizontalMenu.vue";
import CheckpointMicrolearningIcons from "@components/CheckpointMicrolearningIcons/CheckpointMicrolearningIcons.vue";
import { useIQChatStore } from "@components/IQOverlay/store/iqChatStore";
import { formatArticleHtml, shouldPreferArticleHtml } from "@components/utils/formatArticleHtml";
import {
  changeCheckpointV2,
  toggleCheckpointQuizAutopass,
  discardProposedInspectionKit,
  assignVideoUploadTask,
  changeCheckpointQuestion,
  deleteCheckpointQuestion,
  deleteCheckpointProtocol,
  publishProposedInspectionKit,
  removeActiveInspectionKit,
  removeCheckpointMicrolearning,
  deleteProposedVideo,
  saveVideoInspectionInstructions,
  getCheckpointVideoSignedUrl,
} from "@/api/responsibilityAPI";

import { useIQOverlayStore } from "@/store/pinia/iqOverlay";
import { useNotificationStore } from "@/store/pinia/notifications";
import {
  VIDEO_POST_UPLOAD_TOAST_MESSAGE,
  VIDEO_POST_UPLOAD_TOAST_TITLE,
  isPlayableSignedVideoUrl,
  pollCloudflareStreamStatus,
} from "@/utils/videoPostUploadReveal";
import {
  cancelSimulatedCloudflareStream,
  extractCloudflareStreamVideoUidFromEmbeds,
  isCloudflareSignedUrl,
} from "@/utils/simulatedCloudflareStreamWebhook";
import { getCloudflareVideoStatus } from "@/api/proficiencyAPI";
import {
  buildVideoToComponentsCheckpointPatch,
  defaultPassToolbarTabForTestingType,
  isInlineVideoToComponentsPassSuccess,
  suggestedInstructionsHasInspectionProtocol,
  suggestedInstructionsHasQuiz,
} from "@/utils/buildVideoToComponentsCheckpointPatch";
import { needsSignedVideoUrlResolution } from "@/utils/activeCheckpointMicrolearningValidity";
import { ref, computed, getCurrentInstance, watch, onUnmounted, nextTick } from "vue";
import store from "../../account/src/store";

const iqOverlayStore = useIQOverlayStore();
const iqChatStore = useIQChatStore();
const notificationStore = useNotificationStore();
const { proxy } = getCurrentInstance();
const props = defineProps({
  checkpoint: {
    type: Object,
    default: null,
  },
  checkpointInspection: {
    type: Object,
    default: null,
  },
  responsibilityId: {
    type: [String, Number],
    default: null,
  },
  checkpointId: {
    type: [String, Number],
    default: null,
  },
  checkpointPrefix: { type: String, default: "" },
  checkpointCriteria: { type: String, default: "" },
  responsibilityCode: { type: String, default: "" },
  responsibilityName: { type: String, default: "" },
  responsibilityPurpose: { type: String, default: "" },
  checkpointInstructions: { type: String, default: "" },
  microlearningVideoId: { type: [String, Number], default: null },
  /** Parent MDP-452 post-upload reveal window — hide video actions until loader completes. */
  postUploadVideoRevealPending: {
    type: Boolean,
    default: false,
  },
});

/** Simulated Stream webhook + long-running loader (idea-MDP-452) must not run in the legacy editor. */
const ideaMDP452 = computed(() => store.getters["featureFlags/ideaMDP452"]);

const ideaMDP487 = computed(() => store.getters["featureFlags/ideaMDP487"]);

const showActiveVideoMenuItem = computed(() => {
  const activeType =
    props.checkpointInspection?.active_inspection_type ?? props.checkpoint?.active_inspection_type;
  const activeInstructions =
    props.checkpointInspection?.active_inspection_instructions ??
    props.checkpoint?.active_inspection_instructions;
  const proposedInstructions =
    props.checkpointInspection?.proposed_instructions ?? props.checkpoint?.proposed_instructions;
  if (activeType !== "video") return false;
  if (activeInstructions == null || activeInstructions === "") return false;
  if (proposedInstructions != null && proposedInstructions !== "") return false;
  return true;
});

const showProposedVideoMenuItem = computed(() => {
  const type =
    props.checkpointInspection?.proposed_inspection_type ??
    props.checkpoint?.proposed_inspection_type;
  return type === "video";
});

/** Prefer the richer `proposed_article` when inspection vs checkpoint differ (e.g. empty `{}`). */
function preferredProposedArticle(inspection, checkpoint) {
  const a = inspection?.proposed_article;
  const b = checkpoint?.proposed_article;
  const richness = (x) => {
    if (x == null || typeof x !== "object") return -1;
    const body = x.body;
    if (body != null) {
      if (typeof body === "object") return 2;
      if (typeof body === "string" && body.trim() !== "") {
        try {
          const p = JSON.parse(body);
          if (p && typeof p === "object") return 2;
        } catch {
          /* not JSON */
        }
        return 1;
      }
    }
    if (x.title != null && String(x.title).trim() !== "") return 1;
    return Object.keys(x).length > 0 ? 0 : -1;
  };
  const ra = richness(a);
  const rb = richness(b);
  if (ra > rb) return a;
  if (rb > ra) return b;
  return a ?? b ?? null;
}

/** API `proposed_article` for Save/Discard (null ⇒ enable Save after meaningful input). */
const proposedArticleForMenu = computed(() =>
  preferredProposedArticle(props.checkpointInspection, props.checkpoint)
);

const showProposedArticleEditor = computed(() => {
  if (showProposedVideoMenuItem.value) {
    return false;
  }
  const inspectionType =
    props.checkpointInspection?.proposed_inspection_type ??
    props.checkpoint?.proposed_inspection_type;
  const microlearningType =
    props.checkpointInspection?.proposed_microlearning_type ??
    props.checkpoint?.proposed_microlearning_type;
  const ml = String(microlearningType ?? "").toLowerCase();
  const art = proposedArticleForMenu.value;
  const hasTiptapBody =
    art &&
    art.body != null &&
    (typeof art.body === "object" || (typeof art.body === "string" && art.body.trim() !== ""));
  return ml === "article" || inspectionType === "article" || Boolean(hasTiptapBody);
});

watch(showProposedArticleEditor, (visible) => {
  if (visible) proposedArticleMenuTabIndex.value = 0;
});

const proposedArticleHtmlForEditor = computed(() => {
  const raw =
    props.checkpointInspection?.proposed_knowledge_article_html ??
    props.checkpoint?.proposed_knowledge_article_html ??
    null;
  if (raw != null && String(raw).trim() !== "") {
    return formatArticleHtml(raw);
  }
  return formatArticleHtml(proposedArticleForMenu.value);
});

const articleMenuEditorKey = computed(() =>
  props.checkpointId != null ? String(props.checkpointId) : "checkpoint"
);

const proposedArticleBodyForEditor = computed(() => {
  const art = proposedArticleForMenu.value;
  const body = art?.body;
  if (body == null) {
    return null;
  }
  let parsed = null;
  if (typeof body === "object") {
    parsed = body;
  } else if (typeof body === "string") {
    try {
      const json = JSON.parse(body);
      parsed = json && typeof json === "object" ? json : null;
    } catch {
      parsed = null;
    }
  }
  if (!parsed || shouldPreferArticleHtml(parsed)) return null;
  return parsed;
});

const proposedArticleQuizParsed = computed(() => {
  const raw = props.checkpointInspection?.proposed_quiz ?? props.checkpoint?.proposed_quiz ?? null;
  if (raw == null || raw === "") return null;
  return safeParseJson(raw);
});

const proposedArticleQuizPayload = computed(() => {
  const quiz = proposedArticleQuizParsed.value;
  if (!quiz || typeof quiz !== "object" || Array.isArray(quiz)) return null;
  if (Array.isArray(quiz.questions)) return quiz;
  if (quiz.quiz && typeof quiz.quiz === "object" && Array.isArray(quiz.quiz.questions)) {
    return quiz.quiz;
  }
  return null;
});

const proposedArticleQuizQuestions = computed(() => {
  const quiz = proposedArticleQuizParsed.value;
  if (Array.isArray(quiz)) return quiz;
  if (!quiz || typeof quiz !== "object") return [];
  if (Array.isArray(quiz.questions)) return quiz.questions;
  if (Array.isArray(quiz.items)) return quiz.items;
  if (Array.isArray(quiz.quiz?.questions)) return quiz.quiz.questions;
  if (Array.isArray(quiz.quiz?.items)) return quiz.quiz.items;
  return [];
});

/** For `CheckpointMicrolearningMenu` quiz icon: any parsed question row counts as “has quiz”. */
const proposedArticleHasQuizForMenuIcons = computed(
  () => proposedArticleQuizQuestions.value.length > 0
);

const proposedArticleQuizAutopass = computed(
  () =>
    !!(
      props.checkpointInspection?.proposed_quiz_is_autopass ??
      props.checkpoint?.proposed_quiz_is_autopass
    )
);

const proposedArticleQuizMode = computed(() =>
  proposedArticleQuizAutopass.value ? "SKIP_INSPECTION" : "BOTH_REQUIRED"
);

const proposedArticleInspectionProtocolParsed = computed(() => {
  const raw =
    props.checkpointInspection?.proposed_inspection_protocol ??
    props.checkpoint?.proposed_inspection_protocol ??
    null;
  if (raw == null || raw === "") return null;
  return safeParseJson(raw);
});

const proposedArticleInspectionConfirmEveryStep = computed(() => {
  const p = proposedArticleInspectionProtocolParsed.value;
  return !!(p && typeof p === "object" && p.requires_confirmation_at_every_step);
});

const proposedArticleInspectionSteps = computed(() =>
  extractInspectionProtocolStepsFromPayload(proposedArticleInspectionProtocolParsed.value).map(
    mapInstructionStep
  )
);

/** For `CheckpointMicrolearningMenu` inspection icon: derived from `proposed_inspection_protocol` content (steps), not `proposed_has_inspection_protocol`. */
const proposedArticleHasInspectionForMenuIcons = computed(
  () => proposedArticleInspectionSteps.value.length > 0
);

/** Checkpoint API `proposed_inspection_protocol` field is set (not AI / `microlearning_instructions` alone). */
function checkpointProposedInspectionProtocolRaw() {
  const raw =
    props.checkpointInspection?.proposed_inspection_protocol ??
    props.checkpoint?.proposed_inspection_protocol ??
    null;
  return raw != null && raw !== "" ? raw : null;
}

const passToolbarHasCheckpointInspectionProtocol = computed(
  () => proposedArticleInspectionSteps.value.length > 0
);

/** Strip AI-only protocol blobs when checkpoint has no persisted `proposed_inspection_protocol`. */
function passToolbarInlinePayloadFromCheckpoint(ml, proposedProtocolRaw, proposedQuizRaw) {
  if (!ml || typeof ml !== "object") return null;
  const o = { ...ml };
  if (!proposedProtocolRaw) {
    delete o.inspection_instructions;
    delete o.video_instructions;
    if (Array.isArray(o.steps)) o.steps = [];
  }
  if (proposedQuizRaw != null && proposedQuizRaw !== "") {
    const quizParsed = safeParseJson(proposedQuizRaw);
    if (quizParsed != null) o.quiz = quizParsed;
  }
  return o;
}

const hasProposedInstructions = computed(() => {
  const instructions =
    props.checkpointInspection?.proposed_instructions ?? props.checkpoint?.proposed_instructions;
  if (instructions == null || instructions === "") return false;
  if (typeof instructions === "string") return instructions.trim().length > 0;
  if (typeof instructions === "object") return Object.keys(instructions).length > 0;
  return true;
});

/** Backend flag: proposed microlearning video failed video_to_components validation. */
const isProposedVideoMicrolearningMismatch = computed(() => {
  return !!(
    props.checkpointInspection?.proposed_video_microlearning_is_mismatch ??
    props.checkpoint?.proposed_video_microlearning_is_mismatch
  );
});

/**
 * Rich PASS draft from inline video_to_components: TOC + quiz + inspection protocol on checkpoint.
 * Used so we still use the MDP-452 “Source video” lane when `proposed_instructions` holds a TOC JSON
 * string (or legacy bad string) and does not populate `showProposedVideoWithInstructions`.
 */
function microlearningInstructionsLooksLikePassTriple(ml) {
  if (!ml || typeof ml !== "object") return false;
  const statusRaw = ml.validation?.status ?? ml.inspection_instructions?.validation?.status ?? null;
  const statusUpper = typeof statusRaw === "string" ? statusRaw.toUpperCase() : "";
  if (statusUpper !== "PASS") return false;
  const hasQuiz = ml.quiz != null && typeof ml.quiz === "object";
  const hasToc =
    (Array.isArray(ml.table_of_contents?.chapters) && ml.table_of_contents.chapters.length > 0) ||
    (Array.isArray(ml.chapters) && ml.chapters.length > 0);
  const hasProtocol =
    ml.inspection_instructions != null &&
    typeof ml.inspection_instructions === "object" &&
    Array.isArray(ml.inspection_instructions.steps) &&
    ml.inspection_instructions.steps.length > 0;
  return hasQuiz && hasToc && hasProtocol;
}

const checkpointHasMdp452PassMicrolearningDraft = computed(() =>
  microlearningInstructionsLooksLikePassTriple(props.checkpoint?.microlearning_instructions)
);

const showProposedVideoNoInstructions = computed(() => {
  if (!showProposedVideoMenuItem.value) return false;
  if (isProposedVideoMicrolearningMismatch.value) return true;
  if (showProposedVideoWithInstructions.value) return false;
  if (ideaMDP452.value && checkpointHasMdp452PassMicrolearningDraft.value) return true;
  const instructions =
    props.checkpointInspection?.proposed_instructions ?? props.checkpoint?.proposed_instructions;
  return instructions == null || instructions === "";
});

const showProposedVideoWithInstructions = computed(() => {
  if (!showProposedVideoMenuItem.value) return false;
  if (isProposedVideoMicrolearningMismatch.value) return false;
  const parsed = proposedParsedInstructions.value;
  return !!(
    parsed &&
    ((Array.isArray(parsed.steps) && parsed.steps.length > 0) || parsed.summary || parsed.error)
  );
});

function safeParseJson(value) {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

/** Extract src URL from iframe HTML or return the string if already a bare URL. */
function extractVideoUrlFromIframeHtml(iframeHtml) {
  if (!iframeHtml || typeof iframeHtml !== "string") return "";
  if (!iframeHtml.includes("<iframe") && !iframeHtml.includes("src=")) {
    return iframeHtml;
  }
  const srcMatch = iframeHtml.match(/src=\\?"([^"\\]+)/);
  if (srcMatch?.[1]) return srcMatch[1];
  const fallbackMatch = iframeHtml.match(/src="([^"]+)"/);
  if (fallbackMatch?.[1]) return fallbackMatch[1];
  return "";
}

function normalizeCompetenceCategory(category) {
  const normalized = (category || "ability").toLowerCase();
  return normalized === "behavior" ? "behaviour" : normalized;
}

function mapInstructionStep(step = {}) {
  const seqRaw = step.step_sequence ?? step.step_no;
  const step_sequence =
    seqRaw != null && seqRaw !== "" && Number.isFinite(Number(seqRaw)) ? Number(seqRaw) : null;
  return {
    // IQ video-components / suggest paths persist the canonical Content-model
    // shape (step_type + what_to_look_for), while this editor works in the IAAD
    // shape. Mirror what_to_look_for into both prose fields (ER-12476).
    competence_category: normalizeCompetenceCategory(
      step.competence_category || step.competency_category || step.step_type
    ),
    inspector_action:
      step.inspector_action ||
      step.action ||
      step.chapter_title ||
      step.what_to_look_for ||
      step.look_for ||
      "",
    look_for:
      step.look_for ||
      step.chapter_description ||
      step.what_to_look_for ||
      step.inspector_action ||
      "",
    pass_criteria: step.pass_criteria ?? step.chapter_summary ?? "",
    fail_criteria: step.fail_criteria ?? "",
    timestamp_ref:
      step.timestamp_ref ??
      (step.start_time && step.end_time ? `${step.start_time} - ${step.end_time}` : ""),
    video_jump_url: step.video_jump_url ?? "",
    step_sequence,
  };
}

/** Raw steps array from a persisted inspection protocol object (mirrors InspectionEditor / video_to_components). */
function extractInspectionProtocolStepsFromPayload(p) {
  if (!p || typeof p !== "object") return [];
  // Prefer the longest non-empty list so AI flat `{steps}` is not hidden by a
  // parallel empty/partial `inspection_instructions.steps` from older add-step.
  const candidates = [
    p.steps_to_inspect,
    p.instructions?.steps,
    p.instructions?.instructions?.steps,
    p.inspection_instructions?.instructions?.steps,
    p.inspection_instructions?.steps,
    p.steps,
  ].filter((list) => Array.isArray(list) && list.length);
  if (!candidates.length) return [];
  return candidates.reduce((best, list) => (list.length > best.length ? list : best));
}

function mergeProposedInspectionProtocolSnapshot(prev, stepsMapped, confirmEveryStep) {
  const next =
    prev && typeof prev === "object" && !Array.isArray(prev)
      ? JSON.parse(JSON.stringify(prev))
      : {};
  next.requires_confirmation_at_every_step = !!confirmEveryStep;
  const stepsOut = stepsMapped.map((s) => {
    const row = {
      competence_category: s.competence_category,
      inspector_action: s.inspector_action ?? "",
      look_for: s.look_for ?? "",
      pass_criteria: s.pass_criteria ?? "",
      fail_criteria: s.fail_criteria ?? "",
      timestamp_ref: s.timestamp_ref ?? "",
      video_jump_url: s.video_jump_url ?? "",
    };
    if (s.step_sequence != null && Number.isFinite(Number(s.step_sequence))) {
      row.step_sequence = Number(s.step_sequence);
    }
    return row;
  });

  // Write into the longest existing steps list (same preference as extract),
  // so AI flat `{steps}` and nested shapes stay in place on edit/delete.
  const locations = [
    {
      len: Array.isArray(next.steps_to_inspect) ? next.steps_to_inspect.length : 0,
      set: () => {
        next.steps_to_inspect = stepsOut;
      },
    },
    {
      len: Array.isArray(next.steps) ? next.steps.length : 0,
      set: () => {
        next.steps = stepsOut;
      },
    },
    {
      len:
        next.inspection_instructions &&
        typeof next.inspection_instructions === "object" &&
        Array.isArray(next.inspection_instructions.steps)
          ? next.inspection_instructions.steps.length
          : 0,
      set: () => {
        const ii =
          next.inspection_instructions && typeof next.inspection_instructions === "object"
            ? next.inspection_instructions
            : {};
        next.inspection_instructions = { ...ii, steps: stepsOut };
      },
    },
    {
      len:
        next.inspection_instructions?.instructions &&
        typeof next.inspection_instructions.instructions === "object" &&
        Array.isArray(next.inspection_instructions.instructions.steps)
          ? next.inspection_instructions.instructions.steps.length
          : 0,
      set: () => {
        const ii =
          next.inspection_instructions && typeof next.inspection_instructions === "object"
            ? next.inspection_instructions
            : {};
        const inst =
          ii.instructions && typeof ii.instructions === "object" ? ii.instructions : {};
        next.inspection_instructions = {
          ...ii,
          instructions: { ...inst, steps: stepsOut },
        };
      },
    },
    {
      len:
        next.instructions &&
        typeof next.instructions === "object" &&
        Array.isArray(next.instructions.steps)
          ? next.instructions.steps.length
          : 0,
      set: () => {
        const inst =
          next.instructions && typeof next.instructions === "object" ? next.instructions : {};
        next.instructions = { ...inst, steps: stepsOut };
      },
    },
  ];
  const best = locations.reduce(
    (acc, loc) => (loc.len > (acc?.len ?? 0) ? loc : acc),
    null
  );
  if (best && best.len > 0) {
    best.set();
    return next;
  }

  if (Array.isArray(next.steps_to_inspect) || "steps_to_inspect" in next) {
    next.steps_to_inspect = stepsOut;
    return next;
  }
  if (Array.isArray(next.steps) || "steps" in next) {
    next.steps = stepsOut;
    return next;
  }
  if (next.inspection_instructions != null && typeof next.inspection_instructions === "object") {
    const ii = next.inspection_instructions;
    next.inspection_instructions = { ...ii, steps: stepsOut };
    return next;
  }
  if (next.instructions != null && typeof next.instructions === "object") {
    next.instructions = { ...next.instructions, steps: stepsOut };
    return next;
  }
  next.steps = stepsOut;
  return next;
}

function normalizeInspectionInstructions(rawPayload) {
  if (!rawPayload) {
    return { video_url: null, error: null, summary: null, steps: [] };
  }

  const parsedRaw = safeParseJson(rawPayload);
  const payloadWithData =
    parsedRaw &&
    typeof parsedRaw === "object" &&
    "data" in parsedRaw &&
    parsedRaw.data != null &&
    (typeof parsedRaw.data === "object" || typeof parsedRaw.data === "string")
      ? safeParseJson(parsedRaw.data)
      : parsedRaw;
  const payload = payloadWithData && typeof payloadWithData === "object" ? payloadWithData : {};

  const videoUrl = payload.video_url ?? payload.video ?? null;
  const legacySteps = Array.isArray(payload.steps) ? payload.steps.map(mapInstructionStep) : [];
  const legacySummary = payload.summary ?? null;
  const legacyError = payload.error ?? null;

  if (legacySteps.length || legacySummary || legacyError) {
    return {
      video_url: videoUrl,
      error: legacyError,
      summary: legacySummary,
      steps: legacySteps,
    };
  }

  const validation = payload.inspection_instructions?.validation ?? payload.validation ?? null;
  if (validation?.status === "FAIL") {
    return {
      video_url: videoUrl,
      error: validation.reasoning ?? "Video validation failed.",
      summary: null,
      steps: [],
    };
  }

  const rawInstructions =
    payload.inspection_instructions?.instructions ?? payload.instructions ?? null;
  const mappedInstructionSteps = Array.isArray(rawInstructions?.steps)
    ? rawInstructions.steps.map(mapInstructionStep)
    : Array.isArray(rawInstructions)
      ? rawInstructions.map(mapInstructionStep)
      : [];

  const chapterSteps = Array.isArray(payload.table_of_contents?.chapters)
    ? payload.table_of_contents.chapters.map(mapInstructionStep)
    : [];

  return {
    video_url: videoUrl,
    error: null,
    summary: rawInstructions?.overall_rubric ?? null,
    steps: mappedInstructionSteps.length ? mappedInstructionSteps : chapterSteps,
  };
}

const proposedParsedInstructions = computed(() => {
  const raw =
    props.checkpointInspection?.proposed_instructions ??
    props.checkpoint?.proposed_instructions ??
    props.checkpoint?.microlearning_instructions ??
    null;
  return normalizeInspectionInstructions(raw);
});

/** Local override for draft proposed video (upload → simulated Stream webhook). */
const localProposedMicrolearningOverride = ref(null);

/** True from successful upload until post-propose reveal delay completes. Shows loader on banner too. */
const pendingInspectionStreamReveal = ref(false);
/** True after propose-video-inspection-draft until the fixed reveal delay completes. */
const pendingPostProposeStreamReveal = ref(false);
let inspectionStreamRevealTimer = null;

async function fetchCheckpointSignedVideoUrl() {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  if (!accountId || props.responsibilityId == null || props.checkpointId == null) return null;
  try {
    const response = await getCheckpointVideoSignedUrl(
      accountId,
      props.responsibilityId,
      props.checkpointId
    );
    return response?.data?.url ?? null;
  } catch (err) {
    console.error("[CheckpointInspectionInstructions] Error fetching video signed URL:", err);
    return null;
  }
}

const resolvedProposedFallbackVideoUrl = ref(null);
const proposedFallbackRawUrl = computed(() =>
  localProposedMicrolearningOverride.value ? null : props.checkpoint?.microlearning ?? null
);

watch(
  proposedFallbackRawUrl,
  async (url) => {
    resolvedProposedFallbackVideoUrl.value = needsSignedVideoUrlResolution(
      url,
      isCloudflareSignedUrl
    )
      ? await fetchCheckpointSignedVideoUrl()
      : null;
  },
  { immediate: true }
);

const proposedVideoUrl = computed(() => {
  return (
    localProposedMicrolearningOverride.value ??
    resolvedProposedFallbackVideoUrl.value ??
    props.checkpoint?.microlearning ??
    null
  );
});

/**
 * Gate the Stream iframe until Cloudflare has finished encoding. We poll the DASH manifest
 * (200 = ready, 500 = still encoding). Defaults to ready so non-Stream content, unparseable
 * urls, or CORS-blocked probes never hide the player — we only hold it back when the manifest
 * responds with a definitive "not ready".
 */
const streamManifestReady = ref(true);
let streamManifestPollToken = 0;

/**
 * Poll the backend Cloudflare video-status endpoint until the video is ready to stream.
 * `state === "ready"` (or `ready_to_stream`) resolves the gate so the iframe is only shown
 * once encoding is finished — avoids the player error from embedding a still-processing video.
 */
async function waitForCloudflareVideoReady(uid, token) {
  if (!uid) return;
  const { ready } = await pollCloudflareStreamStatus({
    uid,
    pollMs: 1000,
    maxMs: 10 * 60 * 1000,
    shouldContinue: () => token === streamManifestPollToken,
    fetcher: async (id) => {
      const res = await getCloudflareVideoStatus(id);
      return res?.data ?? null;
    },
  });
  if (token === streamManifestPollToken && ready) {
    streamManifestReady.value = true;
  }
}

async function syncStreamManifestReady(url) {
  const source = typeof url === "string" ? url : "";
  const uid = extractCloudflareStreamVideoUidFromEmbeds(source);
  const token = (streamManifestPollToken += 1);

  if (!uid) {
    streamManifestReady.value = true;
    return;
  }

  streamManifestReady.value = false;
  await waitForCloudflareVideoReady(uid, token);
  if (token === streamManifestPollToken) streamManifestReady.value = true;
}

watch(proposedVideoUrl, (url) => syncStreamManifestReady(url), { immediate: true });

const activeParsedInstructions = computed(() => {
  const raw =
    props.checkpointInspection?.active_inspection_instructions ??
    props.checkpoint?.active_inspection_instructions ??
    props.checkpoint?.microlearning_instructions ??
    null;
  return normalizeInspectionInstructions(raw);
});

const resolvedActiveVideoUrl = ref(null);
const activeVideoUrlRaw = computed(() => props.checkpoint?.microlearning ?? null);

watch(
  activeVideoUrlRaw,
  async (url) => {
    resolvedActiveVideoUrl.value = needsSignedVideoUrlResolution(url, isCloudflareSignedUrl)
      ? await fetchCheckpointSignedVideoUrl()
      : null;
  },
  { immediate: true }
);

const activeVideoUrl = computed(() => resolvedActiveVideoUrl.value ?? activeVideoUrlRaw.value);

const isProposedVideoIframe = computed(() => {
  const url = proposedVideoUrl.value;
  return typeof url === "string" && url.trim().toLowerCase().includes("<iframe");
});

const isProposedVideoProcessing = computed(() => {
  const url = proposedVideoUrl.value;
  if (!url || typeof url !== "string") return false;
  const lower = url.toLowerCase();
  return (
    lower.includes("being processed") ||
    lower.includes("will be available in a moment") ||
    lower.includes("processing") ||
    lower.includes("pending")
  );
});

/** Hide Microlearning toolbar + use flat loader until the Stream player is shown */
const isProposedDraftStreamLoading = computed(() => {
  if (props.postUploadVideoRevealPending) return true;
  if (!streamManifestReady.value && !!proposedVideoUrl.value) return true;
  const processingPlaceholder =
    isProposedVideoProcessing.value && !!proposedVideoUrl.value;
  if (ideaMDP452.value) {
    return (
      pendingInspectionStreamReveal.value ||
      pendingPostProposeStreamReveal.value ||
      processingPlaceholder
    );
  }
  return (
    pendingInspectionStreamReveal.value ||
    pendingPostProposeStreamReveal.value ||
    processingPlaceholder
  );
});

const emit = defineEmits([
  "microlearning-upload-click",
  "task-employee-click",
  "propose-click",
  "proposed-knowledge-article-html-change",
  "proposed-knowledge-article-remove",
  "proposed-knowledge-article-save",
  "proposed-knowledge-article-discard",
  "iq-suggest-click",
  "video-uploaded",
  "video-url-ready",
  "refresh-checkpoint",
  "publish",
  "discard",
  "proposed-three-dots-click",
  "propose-changes",
  "remove-active",
  "active-three-dots-click",
  "active-step-click",
]);

const proposedArticleMenuTabIndex = ref(0);

function handleProposedArticleHtmlUpdate(html) {
  emit("proposed-knowledge-article-html-change", { html });
}

const showRemoveMicrolearningDialog = ref(false);
/** @type {import('vue').Ref<'video' | 'article' | null>} */
const removeMicrolearningMode = ref(null);

function openRemoveMicrolearningDialog(mode) {
  removeMicrolearningMode.value = mode;
  showRemoveMicrolearningDialog.value = true;
}

const showTimestampVideoModal = ref(false);
/** @type {import('vue').Ref<string | null>} */
const timestampVideoUrl = ref(null);

/** Accept only absolute http(s) video URLs before they are bound to the iframe `src`. */
function isSafeVideoJumpUrl(url) {
  if (!url || typeof url !== "string") return false;
  const trimmed = url.trim();
  if (!trimmed) return false;
  try {
    const parsed = new URL(trimmed, window.location.origin);
    return parsed.protocol === "https:" || parsed.protocol === "http:";
  } catch {
    return false;
  }
}

function handleTimestampClick(videoJumpUrl) {
  if (!isSafeVideoJumpUrl(videoJumpUrl)) return;
  timestampVideoUrl.value = videoJumpUrl;
  showTimestampVideoModal.value = true;
}

function closeTimestampVideoModal() {
  showTimestampVideoModal.value = false;
  timestampVideoUrl.value = null;
}

function buildArticleMicrolearningRemovePatch() {
  const patch = {
    proposed_inspection_protocol: null,
    proposed_microlearning_type: null,
    proposed_quiz: null,
    proposed_quiz_is_autopass: false,
    proposed_knowledge_article_html: null,
    proposed_article: null,
  };
  if (props.checkpoint?.proposed_inspection_type === "article") {
    patch.proposed_inspection_type = null;
  }
  return patch;
}

async function removeArticleProposedMicrolearning() {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  if (!accountId || props.responsibilityId == null || props.checkpointId == null) {
    notificationStore.error("Missing account or checkpoint information.");
    return false;
  }
  proxy?.$loader?.setAppLoading(true);
  try {
    await changeCheckpointV2(
      accountId,
      props.responsibilityId,
      props.checkpointId,
      buildArticleMicrolearningRemovePatch()
    );
    emit("refresh-checkpoint");
    emit("proposed-knowledge-article-remove");
    return true;
  } catch (err) {
    console.error(
      "[CheckpointInspectionInstructions] Error removing proposed article microlearning:",
      err
    );
    notificationStore.error(
      err?.response?.data?.detail ??
        err?.response?.data?.message ??
        "Could not remove microlearning."
    );
    return false;
  } finally {
    proxy?.$loader?.setAppLoading(false);
  }
}

async function handleRemoveMicrolearningDialogConfirm() {
  const mode = removeMicrolearningMode.value;
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  if (!accountId || props.responsibilityId == null || props.checkpointId == null) {
    notificationStore.error("Missing account or checkpoint information.");
    return;
  }
  try {
    await removeCheckpointMicrolearning(accountId, props.responsibilityId, props.checkpointId);
    if (mode === "article") {
      await removeArticleProposedMicrolearning();
      return;
    }
    const ok = await handleDiscard();
    if (ok) {
      handleInlineFromVideoIqDismiss();
    }
  } catch (err) {
    console.error("[CheckpointInspectionInstructions] Error removing microlearning:", err);
    notificationStore.error(
      err?.response?.data?.detail ??
        err?.response?.data?.message ??
        "Could not remove microlearning."
    );
  } finally {
    showRemoveMicrolearningDialog.value = false;
    removeMicrolearningMode.value = null;
  }
}

function handleProposedArticleSave(payload) {
  emit("proposed-knowledge-article-save", payload);
}

function handleProposedArticleDiscard() {
  emit("proposed-knowledge-article-discard");
}

function handleProposedArticleMenuTabChange(index) {
  const nextIndex = Number(index);
  proposedArticleMenuTabIndex.value = Number.isFinite(nextIndex) ? nextIndex : 0;
}

watch(
  [proposedArticleMenuTabIndex, proposedArticleQuizAutopass],
  ([tab, autopass]) => {
    if (autopass && tab === 2) {
      proposedArticleMenuTabIndex.value = 1;
    }
  },
  { immediate: true }
);

function inspectionStreamWebhookKey() {
  return props.checkpointId != null ? `cf-inspection-${props.checkpointId}` : null;
}

function clearInspectionStreamRevealTimer() {
  if (inspectionStreamRevealTimer != null) {
    clearTimeout(inspectionStreamRevealTimer);
    inspectionStreamRevealTimer = null;
  }
}

function clearInspectionStreamSimulation() {
  const key = inspectionStreamWebhookKey();
  if (key) cancelSimulatedCloudflareStream(key);
  clearInspectionStreamRevealTimer();
  pendingInspectionStreamReveal.value = false;
  pendingPostProposeStreamReveal.value = false;
  localProposedMicrolearningOverride.value = null;
}

/**
 * Show the processing loader immediately after upload; reveal is driven by handleVideoUrlReady
 * once propose-video-inspection-draft and checkpoint refresh complete.
 */
function scheduleInspectionStreamSimulationFromUpload() {
  if (!ideaMDP452.value) {
    pendingInspectionStreamReveal.value = false;
    localProposedMicrolearningOverride.value = null;
    return;
  }

  const key = inspectionStreamWebhookKey();
  if (key) cancelSimulatedCloudflareStream(key);

  pendingInspectionStreamReveal.value = true;
  localProposedMicrolearningOverride.value =
    "Video is being processed — your upload completed successfully.";
}

watch(
  () => props.checkpointId,
  (newId, oldId) => {
    if (oldId != null) {
      cancelSimulatedCloudflareStream(`cf-inspection-${oldId}`);
    }
    clearInspectionStreamRevealTimer();
    pendingInspectionStreamReveal.value = false;
    pendingPostProposeStreamReveal.value = false;
    localProposedMicrolearningOverride.value = null;
  }
);

onUnmounted(() => {
  const key = inspectionStreamWebhookKey();
  if (key) cancelSimulatedCloudflareStream(key);
  clearInspectionStreamRevealTimer();
  pendingInspectionStreamReveal.value = false;
  pendingPostProposeStreamReveal.value = false;
});

const showUploadDialog = ref(false);
const pendingUploadUrl = ref(null);
const pendingVideoId = ref(null);

watch(showUploadDialog, (open) => {
  if (!open) {
    pendingUploadUrl.value = null;
    pendingVideoId.value = null;
  }
});
const showTaskEmployeeDialog = ref(false);
const showPublishDialog = ref(false);
const showGenerateVideoMicrolearningDialog = ref(false);
const showDeleteEntireQuizDialog = ref(false);
const deleteEntireQuizSubmitting = ref(false);
const showInlineFromVideoIq = ref(false);
const inlineFromVideoIqUserPrompt = ref("");
const inlineVideoToComponentsSelection = ref(null);
const inlineFromVideoSuggestedInstructions = ref(null);
const pendingVideoToComponentsSelection = ref(null);
const pendingInlineReplaceReset = ref(false);

const generateVideoMicrolearningDialogInitialVideoType = computed(() => {
  const fromSelection = inlineVideoToComponentsSelection.value?.videoType;
  if (fromSelection === "toc" || fromSelection === "walkthrough") {
    return fromSelection;
  }
  return "toc";
});

const generateVideoMicrolearningDialogInitialTestingType = computed(() => {
  const fromSelection = inlineVideoToComponentsSelection.value?.testingType;
  if (
    fromSelection === "quiz_only" ||
    fromSelection === "inspection_only" ||
    fromSelection === "both"
  ) {
    return fromSelection;
  }
  return "both";
});

/** Opens generate dialog only — keeps fail-review state until user confirms Generate. */
function openRegenerateInspectionDialog() {
  showGenerateVideoMicrolearningDialog.value = true;
}

/** Selected tab in PASS toolbar `HorizontalMenu` (0 = video, 1 = quiz, 2 = protocol when visible). */
const passToolbarMenuTabIndex = ref(0);

/** Mirrors checkpoint `proposed_quiz_is_autopass` for inline PASS quiz toolbar. */
const inlinePassQuizAutopass = computed(
  () =>
    !!(
      props.checkpointInspection?.proposed_quiz_is_autopass ??
      props.checkpoint?.proposed_quiz_is_autopass
    )
);

const inlinePassQuizMode = computed(() =>
  inlinePassQuizAutopass.value ? "SKIP_INSPECTION" : "BOTH_REQUIRED"
);

const passToolbarHorizontalItems = computed(() => {
  const base = [
    { title: "Video", count: null },
    { title: "Quiz", count: null },
  ];
  if (inlinePassQuizAutopass.value) return base;
  return [...base, { title: "Inspection Protocol", count: null }];
});

const passToolbarHorizontalMenuKey = computed(
  () => `pass-toolbar-${inlinePassQuizAutopass.value ? "autopass" : "manual"}`
);

function handlePassToolbarHorizontalChange(index) {
  const n = Number(index);
  passToolbarMenuTabIndex.value = Number.isFinite(n) ? n : 0;
}

/** Numeric tab index for comparisons and HorizontalMenu `:value` (avoids `"1" === 1` mismatches). */
const passToolbarTabIndexNum = computed(() => {
  const v = passToolbarMenuTabIndex.value;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
});

watch(
  [passToolbarMenuTabIndex, inlinePassQuizAutopass],
  ([tab, autopass]) => {
    if (autopass && tab === 2) {
      passToolbarMenuTabIndex.value = 1;
    }
  },
  { immediate: true }
);

/**
 * PASS toolbar with Microlearning / Quiz / Inspection tabs when the payload includes quiz + protocol data.
 * Do not require `chapters` to be an array — some PASS payloads omit or reshape it and Quiz would never render.
 */
const inlinePassChapterSteps = computed(() => {
  const s = inlineFromVideoSuggestedInstructions.value;
  const tocChapters = s?.table_of_contents?.chapters;
  const mappedChapters = Array.isArray(s?.chapters) ? s.chapters : [];

  /** Prefer raw TOC rows so MicrolearningChapterCard gets chapter_title / times / summaries */
  if (Array.isArray(tocChapters) && tocChapters.length > 0) {
    return tocChapters.map((ch) => ({
      ...mapInstructionStep(ch),
      chapter_title: ch.chapter_title ?? "",
      chapter_description: ch.chapter_description ?? "",
      chapter_summary: ch.chapter_summary ?? "",
      start_time: ch.start_time ?? "",
      end_time: ch.end_time ?? "",
    }));
  }

  return mappedChapters;
});

const inlinePassChapterVideoUrl = computed(() => {
  const s = inlineFromVideoSuggestedInstructions.value;
  return s?.video_url || proposedParsedInstructions.value.video_url || proposedVideoUrl.value || "";
});

const inlinePassChapterSummary = computed(() => {
  return inlineFromVideoSuggestedInstructions.value?.microlearning_summary ?? null;
});

/** Full `{ quiz_title, questions: [...] }` for QuizEditor when API stores an object */
const inlinePassQuizPayload = computed(() => {
  const s = inlineFromVideoSuggestedInstructions.value;
  if (!s?.quiz || typeof s.quiz !== "object" || Array.isArray(s.quiz)) return null;
  if (!Array.isArray(s.quiz.questions)) return null;
  return s.quiz;
});

const inlinePassQuizQuestions = computed(() => {
  const s = inlineFromVideoSuggestedInstructions.value;
  if (!s) return [];
  const quiz = s.quiz;
  if (Array.isArray(quiz)) return quiz;
  if (quiz != null && typeof quiz === "object") {
    if (Array.isArray(quiz.questions)) return quiz.questions;
    if (Array.isArray(quiz.items)) return quiz.items;
  }
  if (Array.isArray(s.questions)) return s.questions;
  return [];
});

async function handleInlinePassQuizAutopassChange(nextValue) {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  if (!accountId || props.responsibilityId == null || props.checkpointId == null) {
    return;
  }
  try {
    await toggleCheckpointQuizAutopass(
      accountId,
      props.responsibilityId,
      props.checkpointId,
      !!nextValue
    );
    emit("refresh-checkpoint");
  } catch (err) {
    console.error("[CheckpointInspectionInstructions] quiz_is_autopass toggle:", err);
    notificationStore.error(
      err?.response?.data?.detail ??
        err?.response?.data?.message ??
        "Could not update quiz auto-pass setting."
    );
  }
}

async function persistProposedArticleInspectionProtocol(stepsMapped, confirmEveryStep) {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  if (!accountId || props.responsibilityId == null || props.checkpointId == null) {
    return false;
  }
  const confirm =
    confirmEveryStep !== undefined && confirmEveryStep !== null
      ? !!confirmEveryStep
      : proposedArticleInspectionConfirmEveryStep.value;
  const merged = mergeProposedInspectionProtocolSnapshot(
    proposedArticleInspectionProtocolParsed.value,
    stepsMapped,
    confirm
  );
  proxy?.$loader.setAppLoading(true);
  try {
    await changeCheckpointV2(accountId, props.responsibilityId, props.checkpointId, {
      proposed_inspection_protocol: JSON.stringify(merged),
    });
    emit("refresh-checkpoint");
    return true;
  } catch (err) {
    console.error("[CheckpointInspectionInstructions] proposed_inspection_protocol update:", err);
    notificationStore.error(
      err?.response?.data?.detail ??
        err?.response?.data?.message ??
        "Could not update inspection protocol."
    );
    return false;
  } finally {
    proxy?.$loader.setAppLoading(false);
  }
}

async function handleProposedArticleInspectionConfirmEveryStepChange(nextValue) {
  await persistProposedArticleInspectionProtocol(proposedArticleInspectionSteps.value, !!nextValue);
}

function buildAddInspectionStepApiPayload(step) {
  const raw = String(step?.competence_category ?? "ability").toLowerCase();
  let competence_category = "Ability";
  if (raw === "knowledge") competence_category = "Knowledge";
  else if (raw === "behaviour" || raw === "behavior") competence_category = "Behavior";

  return {
    step: {
      competence_category,
      inspector_action: step?.inspector_action != null ? String(step.inspector_action).trim() : "",
      look_for: step?.look_for != null ? String(step.look_for).trim() : "",
      pass_criteria: step?.pass_criteria != null ? String(step.pass_criteria).trim() : "",
      fail_criteria: step?.fail_criteria != null ? String(step.fail_criteria).trim() : "",
    },
  };
}

async function handleAddInspectionStep(payload) {
  const step = payload?.step;
  const complete = typeof payload?.complete === "function" ? payload.complete : null;
  if (props.responsibilityId == null || props.checkpointId == null) {
    complete?.(new Error("Missing checkpoint context"));
    return;
  }

  const body = buildAddInspectionStepApiPayload(step);
  const s = body.step;
  if (!s.inspector_action || !s.look_for || !s.pass_criteria || !s.fail_criteria) {
    notificationStore.error("Inspect, look for, pass, and fail fields are required.");
    complete?.(new Error("validation"));
    return;
  }

  const existing = proposedArticleInspectionSteps.value;
  const maxSeq = existing.reduce((max, row) => {
    const n = Number(row?.step_sequence);
    return Number.isFinite(n) && n > max ? n : max;
  }, 0);
  const nextSteps = [
    ...existing,
    mapInstructionStep({
      ...s,
      step_sequence: maxSeq + 1,
    }),
  ];

  const ok = await persistProposedArticleInspectionProtocol(nextSteps);
  complete?.(ok ? null : new Error("persist failed"));
}

async function handleUpdateInspectionStep(payload) {
  const index = payload?.index;
  const step = payload?.step;
  const complete = typeof payload?.complete === "function" ? payload.complete : null;
  if (index == null || index < 0 || !step) {
    complete?.(new Error("Invalid payload"));
    return;
  }
  if (props.responsibilityId == null || props.checkpointId == null) {
    notificationStore.error("Missing account or checkpoint context.");
    complete?.(new Error("Missing checkpoint context"));
    return;
  }

  const body = buildAddInspectionStepApiPayload(step);
  const s = body.step;
  if (!s.inspector_action || !s.look_for || !s.pass_criteria || !s.fail_criteria) {
    notificationStore.error("Inspect, look for, pass, and fail fields are required.");
    complete?.(new Error("validation"));
    return;
  }

  const existing = proposedArticleInspectionSteps.value;
  if (index >= existing.length) {
    complete?.(new Error("Invalid payload"));
    return;
  }
  const prev = existing[index] || {};
  const nextSteps = existing.map((row, i) =>
    i === index
      ? mapInstructionStep({
          ...s,
          timestamp_ref: step.timestamp_ref ?? prev.timestamp_ref,
          video_jump_url: step.video_jump_url ?? prev.video_jump_url,
          step_sequence: prev.step_sequence ?? index + 1,
        })
      : row
  );

  const ok = await persistProposedArticleInspectionProtocol(nextSteps);
  complete?.(ok ? null : new Error("persist failed"));
}

async function handleDeleteInspectionStep(payload) {
  const index = payload?.index;
  const complete = typeof payload?.complete === "function" ? payload.complete : null;
  if (index == null || index < 0) {
    complete?.(new Error("Invalid payload"));
    return;
  }
  if (props.responsibilityId == null || props.checkpointId == null) {
    notificationStore.error("Missing account or checkpoint context.");
    complete?.(new Error("Missing checkpoint context"));
    return;
  }

  const existing = proposedArticleInspectionSteps.value;
  if (index >= existing.length) {
    complete?.(new Error("Invalid payload"));
    return;
  }
  const nextSteps = existing.filter((_, i) => i !== index);

  const ok = await persistProposedArticleInspectionProtocol(nextSteps);
  complete?.(ok ? null : new Error("persist failed"));
}

function mapAddQuestionPayload(payload) {
  const questionText = payload?.question != null ? String(payload.question).trim() : "";
  const answers = Array.isArray(payload?.answers) ? payload.answers : [];
  const qAnswers = [];
  let aNo = 1;
  for (const item of answers) {
    const aText = item?.answer != null ? String(item.answer).trim() : "";
    if (!aText) continue;
    const isCorrect = item?.checked === true ? 1 : 0;
    const correctDescription =
      item?.correct_description != null ? String(item.correct_description).trim() : "";
    qAnswers.push({
      a_no: aNo,
      a_text: aText,
      isCorrect,
      correct_description: isCorrect === 1 ? correctDescription : "",
    });
    aNo += 1;
  }

  return {
    question: {
      q_text: questionText,
      q_answers: qAnswers,
    },
  };
}

/** One quiz row from `QuizEditor` / draggable → API `{ q_no, q_text, q_answers }` (`q_no` is order index). */
function editorQuizRowToApiQuestion(row, sequentialQNo) {
  const q_text = String(row?.q_text ?? row?.text ?? row?.question ?? "").trim();
  const rawAnswers = Array.isArray(row?.q_answers)
    ? row.q_answers
    : Array.isArray(row?.answers)
      ? row.answers
      : [];
  const q_answers = [];
  let aNo = 1;
  for (const a of rawAnswers) {
    const a_text = String(a?.a_text ?? a?.text ?? a?.answer ?? "").trim();
    if (!a_text) continue;
    const isCorrectFlag =
      a?.isCorrect === 1 ||
      a?.isCorrect === true ||
      a?.is_correct === true ||
      Number(a?.isCorrect) === 1;
    const isCorrect = isCorrectFlag ? 1 : 0;
    const correct_description = isCorrect === 1 ? String(a?.correct_description ?? "").trim() : "";
    q_answers.push({
      a_no: aNo,
      a_text,
      isCorrect,
      correct_description: isCorrect === 1 ? correct_description : "",
    });
    aNo += 1;
  }
  return {
    q_no: sequentialQNo,
    q_text,
    q_answers,
  };
}

function mergeProposedQuizRootWithQuestions(parsedRoot, newQuestions) {
  if (Array.isArray(parsedRoot)) {
    return { questions: newQuestions };
  }
  if (!parsedRoot || typeof parsedRoot !== "object") {
    const inner = proposedArticleQuizPayload.value;
    const quiz_title = inner && typeof inner.quiz_title === "string" ? inner.quiz_title : "";
    return { quiz_title, questions: newQuestions };
  }
  if (parsedRoot.quiz && typeof parsedRoot.quiz === "object" && !Array.isArray(parsedRoot.quiz)) {
    return {
      ...parsedRoot,
      quiz: {
        ...parsedRoot.quiz,
        questions: newQuestions,
      },
    };
  }
  return {
    ...parsedRoot,
    questions: newQuestions,
  };
}

async function handleReorderQuestions({ questions: rows }) {
  if (!Array.isArray(rows) || rows.length === 0) return;
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  if (!accountId || props.responsibilityId == null || props.checkpointId == null) {
    notificationStore.error("Missing checkpoint context.");
    return;
  }

  const apiQuestions = rows.map((row, i) => editorQuizRowToApiQuestion(row, i + 1));
  const hasInvalid = apiQuestions.some(
    (q) => !q.q_text || !Array.isArray(q.q_answers) || !q.q_answers.length
  );
  if (hasInvalid) {
    notificationStore.error("Could not save quiz order: invalid question data.");
    return;
  }

  await persistProposedArticleQuiz(apiQuestions);
}

async function persistProposedArticleQuiz(apiQuestions) {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  if (!accountId || props.responsibilityId == null || props.checkpointId == null) return false;

  const merged = mergeProposedQuizRootWithQuestions(proposedArticleQuizParsed.value, apiQuestions);
  const proposed_quiz = JSON.stringify(merged);

  proxy?.$loader.setAppLoading(true);
  try {
    await changeCheckpointV2(accountId, props.responsibilityId, props.checkpointId, {
      proposed_quiz,
    });
    emit("refresh-checkpoint");
    return true;
  } catch (err) {
    console.error("[CheckpointInspectionInstructions] proposed_quiz update:", err);
    notificationStore.error(
      err?.response?.data?.detail ??
        err?.response?.data?.message ??
        "Could not update quiz."
    );
    return false;
  } finally {
    proxy?.$loader.setAppLoading(false);
  }
}

async function handleAddQuestion(payload) {
  const complete = typeof payload?.complete === "function" ? payload.complete : null;
  if (props.responsibilityId == null || props.checkpointId == null) {
    complete?.(new Error("Missing checkpoint context"));
    return;
  }

  const requestPayload = mapAddQuestionPayload(payload);
  const q = requestPayload.question;
  const answersList = Array.isArray(q?.q_answers) ? q.q_answers : [];
  const hasCorrect = answersList.some((a) => Number(a.isCorrect) === 1);
  if (
    !q?.q_text ||
    answersList.length < MIN_QUIZ_ANSWERS ||
    answersList.length > MAX_QUIZ_ANSWERS ||
    !hasCorrect
  ) {
    notificationStore.error(QUIZ_QUESTION_INVALID_MESSAGE);
    complete?.(new Error("validation"));
    return;
  }

  const existing = proposedArticleQuizQuestions.value;
  const existingApi = existing.map((row, i) => {
    const qNo = Number(row?.q_no);
    return editorQuizRowToApiQuestion(row, Number.isFinite(qNo) && qNo > 0 ? qNo : i + 1);
  });
  const maxQNo = existingApi.reduce((max, row) => {
    const n = Number(row?.q_no);
    return Number.isFinite(n) && n > max ? n : max;
  }, 0);
  const apiQuestions = [
    ...existingApi,
    {
      q_no: maxQNo + 1,
      q_text: q.q_text,
      q_answers: answersList,
    },
  ];

  const ok = await persistProposedArticleQuiz(apiQuestions);
  complete?.(ok ? null : new Error("persist failed"));
}

async function handleUpdateQuestion(payload) {
  const complete = typeof payload?.complete === "function" ? payload.complete : null;
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  if (!accountId || props.responsibilityId == null || props.checkpointId == null) {
    notificationStore.error("Missing checkpoint context.");
    complete?.(new Error("Missing checkpoint context"));
    return;
  }

  const qNo = payload?.q_no;
  if (qNo == null || qNo === "") {
    notificationStore.error("Missing question number. Try refreshing the page.");
    complete?.(new Error("missing q_no"));
    return;
  }

  const requestPayload = mapAddQuestionPayload(payload);
  const q = requestPayload.question;
  const answersList = Array.isArray(q?.q_answers) ? q.q_answers : [];
  const hasCorrect = answersList.some((a) => Number(a.isCorrect) === 1);
  if (
    !q?.q_text ||
    answersList.length < MIN_QUIZ_ANSWERS ||
    answersList.length > MAX_QUIZ_ANSWERS ||
    !hasCorrect
  ) {
    notificationStore.error(QUIZ_QUESTION_INVALID_MESSAGE);
    complete?.(new Error("validation"));
    return;
  }

  proxy?.$loader.setAppLoading(true);
  try {
    await changeCheckpointQuestion(
      accountId,
      props.responsibilityId,
      props.checkpointId,
      qNo,
      requestPayload
    );
    emit("refresh-checkpoint");
    complete?.(null);
  } catch (err) {
    console.error("[CheckpointInspectionInstructions] update question:", err);
    notificationStore.error(
      err?.response?.data?.detail ??
        err?.response?.data?.message ??
        "Could not update the question."
    );
    complete?.(err);
  } finally {
    proxy?.$loader.setAppLoading(false);
  }
}

async function handleDeleteQuestion(payload) {
  const complete = typeof payload?.complete === "function" ? payload.complete : null;
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  if (!accountId || props.responsibilityId == null || props.checkpointId == null) {
    notificationStore.error("Missing checkpoint context.");
    complete?.(new Error("Missing checkpoint context"));
    return;
  }

  const qNo = payload?.q_no;
  if (qNo == null || qNo === "") {
    notificationStore.error(
      "This question cannot be removed from the server. Try refreshing the page."
    );
    complete?.(new Error("missing q_no"));
    return;
  }

  try {
    await deleteCheckpointQuestion(accountId, props.responsibilityId, props.checkpointId, qNo);
    emit("refresh-checkpoint");
    complete?.(null);
  } catch (err) {
    console.error("[CheckpointInspectionInstructions] delete question:", err);
    notificationStore.error(
      err?.response?.data?.detail ??
        err?.response?.data?.message ??
        "Could not delete the question."
    );
    complete?.(err);
  }
}

function handleQuizBulkMenuAction(payload) {
  if (payload?.action === "delete-quiz") {
    showDeleteEntireQuizDialog.value = true;
  }
}

async function onConfirmDeleteEntireQuizDialog() {
  if (deleteEntireQuizSubmitting.value) return;
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  if (!accountId || props.responsibilityId == null || props.checkpointId == null) {
    notificationStore.error("Missing checkpoint context.");
    return;
  }

  deleteEntireQuizSubmitting.value = true;
  try {
    await changeCheckpointV2(accountId, props.responsibilityId, props.checkpointId, {
      proposed_quiz: null,
      proposed_quiz_is_autopass: false,
    });
    emit("refresh-checkpoint");
    showDeleteEntireQuizDialog.value = false;
  } catch (err) {
    console.error("[CheckpointInspectionInstructions] clear proposed quiz:", err);
    notificationStore.error(
      err?.response?.data?.detail ?? err?.response?.data?.message ?? "Could not delete the quiz."
    );
  } finally {
    deleteEntireQuizSubmitting.value = false;
  }
}

async function handleDeleteInspectionProtocolRequested(payload) {
  const complete = typeof payload?.complete === "function" ? payload.complete : null;
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  if (!accountId || props.responsibilityId == null || props.checkpointId == null) {
    notificationStore.error("Missing checkpoint context.");
    complete?.(new Error("Missing checkpoint context"));
    return;
  }

  try {
    await deleteCheckpointProtocol(accountId, props.responsibilityId, props.checkpointId);
    emit("refresh-checkpoint");
    complete?.(null);
  } catch (err) {
    console.error("[CheckpointInspectionInstructions] delete protocol:", err);
    notificationStore.error(
      err?.response?.data?.detail ??
        err?.response?.data?.message ??
        "Could not delete the inspection protocol."
    );
    complete?.(err);
  }
}

const inlinePassProtocolSummary = computed(() => {
  const s = inlineFromVideoSuggestedInstructions.value;
  const fromNested = s?.inspection_instructions?.instructions?.overall_rubric;
  if (fromNested != null) return fromNested;
  /** Legacy normalized PASS: rubric lives on `summary`, no `inspection_instructions` wrapper */
  return s?.summary ?? null;
});

const inlinePassProtocolSteps = computed(() => {
  const s = inlineFromVideoSuggestedInstructions.value;
  const nestedSteps = s?.inspection_instructions?.instructions?.steps;
  if (Array.isArray(nestedSteps) && nestedSteps.length > 0) {
    return nestedSteps.map(mapInstructionStep);
  }
  const directInspectionSteps = s?.inspection_instructions?.steps;
  if (Array.isArray(directInspectionSteps) && directInspectionSteps.length > 0) {
    return directInspectionSteps.map(mapInstructionStep);
  }
  /** Legacy: flat `steps` on suggestions object */
  const top = s?.steps;
  if (Array.isArray(top)) return top.map(mapInstructionStep);
  return [];
});

/** Same quiz icon semantics as `CheckpointMicrolearningMenu` (`iconsQuizKind`): None | Normal | Autopass. */
const inlinePassToolbarHasQuiz = computed(
  () => proposedArticleQuizQuestions.value.length > 0 || inlinePassQuizQuestions.value.length > 0
);

const inlinePassToolbarIconsQuiz = computed(() => {
  if (!inlinePassToolbarHasQuiz.value) return "None";
  if (proposedArticleQuizAutopass.value) return "Autopass";
  return "Normal";
});

/** Inspection icon: checkpoint `proposed_inspection_protocol` only (not AI TOC / `microlearning_instructions`). */
const inlinePassToolbarIconsInspection = computed(
  () => proposedArticleHasInspectionForMenuIcons.value
);

const showInlineFromVideoFailReview = computed(() => {
  return !!(
    ideaMDP452.value &&
    showInlineFromVideoIq.value &&
    inlineFromVideoSuggestedInstructions.value &&
    inlineFromVideoSuggestedInstructions.value.error
  );
});

const showInlineFromVideoPassToolbar = computed(() => {
  if (
    !ideaMDP452.value ||
    !showInlineFromVideoIq.value ||
    !inlineFromVideoSuggestedInstructions.value
  ) {
    return false;
  }
  const s = inlineFromVideoSuggestedInstructions.value;
  if (s.error) return false;
  return isInlineVideoToComponentsPassSuccess(s, inlineVideoToComponentsSelection.value);
});

const isDiscardLoading = ref(false);

const passToolbarMoreMenuItems = computed(() => [
  {
    name: "Replace",
    value: "replace",
    icon: "icons/dropdown/upload-microlearning.svg",
  },
  { separator: true },
  {
    name: "Delete",
    value: "delete",
    icon: "icons/dropdown/trash.svg",
    destructive: true,
    negative: true,
    disabled: isDiscardLoading.value,
  },
]);

function handlePassToolbarMenuClick(item) {
  if (!item || item.separator || item.disabled) return;
  if (item.value === "replace") handleInlineFailReplace();
  else if (item.value === "delete") openDeleteMicrolearningVideoDialog();
}
const isPublishLoading = ref(false);
const isRemoveActiveLoading = ref(false);

const checkpointDescriptionForTaskDialog = computed(() => {
  return [props.checkpointPrefix, props.checkpointCriteria].filter(Boolean).join(" ") || "";
});

function handleTaskEmployeeClick() {
  const hasExistingTask =
    props.checkpointInspection?.proposed_video_upload_task ??
    props.checkpoint?.proposed_video_upload_task;
  if (hasExistingTask != null && hasExistingTask !== false) {
    notificationStore.error("Checkpoint already has a task to upload");
    return;
  }
  showTaskEmployeeDialog.value = true;
}

async function handleTaskEmployeeAssign(payload) {
  if (!payload?.employee) return;
  const deadline = payload.deadline;
  let deadlineStr = null;
  if (typeof deadline === "string" && /^\d{4}-\d{2}-\d{2}$/.test(deadline)) {
    deadlineStr = deadline;
  } else if (deadline instanceof Date && !Number.isNaN(deadline.getTime())) {
    deadlineStr = deadline.toISOString().slice(0, 10);
  } else if (deadline?.toISOString) {
    deadlineStr = deadline.toISOString().slice(0, 10);
  } else {
    const d = new Date(deadline);
    if (!Number.isNaN(d.getTime())) deadlineStr = d.toISOString().slice(0, 10);
  }
  if (!deadlineStr) return;
  const notify = payload.employee.access === true ? payload.notifyEmployee === true : false;
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  if (!accountId || !props.responsibilityId || !props.checkpointId) return;
  proxy?.$loader.setAppLoading(true);
  try {
    await assignVideoUploadTask(accountId, props.responsibilityId, props.checkpointId, {
      assignee_id: payload.employee.id,
      deadline: deadlineStr,
      notify,
    });
    showTaskEmployeeDialog.value = false;
    notificationStore.success("Task assigned successfully");
    emit("refresh-checkpoint");
  } catch (error) {
    const message =
      error?.response?.data?.message ?? error?.response?.data?.detail ?? "Failed to assign task.";
    notificationStore.error(message);
  } finally {
    proxy?.$loader.setAppLoading(false);
  }
}

// Reused from CheckpointMicrolearningVideoCard: open IQ overlay for "Suggest Instructions from video"
function buildGenerateVideoMicrolearningPromptSuffix(payload) {
  const { videoType, testingType } = payload;
  const videoLabel =
    videoType === "toc"
      ? "Video with Table of Contents"
      : "Step by Step Annotated Video Walkthrough";
  const testingLabel =
    testingType === "quiz_only"
      ? "Generate only Quiz with Auto Pass"
      : testingType === "inspection_only"
        ? "Generate only Inspection Protocol"
        : "Generate both Quiz and Inspection Protocol";
  return `Selected microlearning video type: ${videoLabel}. Selected testing/inspection: ${testingLabel}.`;
}

function buildFromVideoIqUserPrompt(extraPromptSuffix = "") {
  const checkpointInfo = [props.checkpointPrefix, props.checkpointCriteria]
    .filter(Boolean)
    .join(" ");
  const responsibilityInfo = [props.responsibilityCode, props.responsibilityName]
    .filter(Boolean)
    .join(" ");
  const base = `Pls suggest Inspection instructions from video of Checkpoint: ${checkpointInfo} belonging to responsibility ${responsibilityInfo}`;
  return extraPromptSuffix ? `${base} ${extraPromptSuffix}` : base;
}

function openIqVideoSuggestOverlay(extraPromptSuffix = "") {
  const prompt = buildFromVideoIqUserPrompt(extraPromptSuffix);
  iqOverlayStore.openOverlay({
    prompt,
    type: "videoInstructions",
    useCustomLoader: true,
    videoUrl: proposedVideoUrl.value,
    checkpointId: props.checkpointId,
    responsibilityId: props.responsibilityId,
    checkpointPrefix: props.checkpointPrefix,
    checkpointCriteria: props.checkpointCriteria,
    responsibilityCode: props.responsibilityCode,
    responsibilityName: props.responsibilityName,
    responsibilityPurpose: props.responsibilityPurpose,
    checkpointInstructions: props.checkpointInstructions,
    videoToComponentsSelection: pendingVideoToComponentsSelection.value,
  });
  pendingVideoToComponentsSelection.value = null;
  emit("iq-suggest-click");
}

async function persistInlineVideoToComponentsResult(suggestedInstructions, mode) {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  if (!accountId || props.responsibilityId == null || props.checkpointId == null) {
    return;
  }
  try {
    let patch = buildVideoToComponentsCheckpointPatch({
      suggestedInstructions,
      mode,
      microlearningSelection: inlineVideoToComponentsSelection.value,
    });

    if (mode === "fail") {
      const videoUrl =
        suggestedInstructions?.video_url ??
        (extractVideoUrlFromIframeHtml(String(proposedVideoUrl.value || "")) || null);
      if (videoUrl && patch.proposed_instructions) {
        const parsed = JSON.parse(patch.proposed_instructions);
        patch = {
          ...patch,
          proposed_instructions: JSON.stringify({ ...parsed, video_url: videoUrl }),
        };
      }
    }

    await changeCheckpointV2(accountId, props.responsibilityId, props.checkpointId, patch);

    if (mode === "pass") {
      const inspectionKit = store.getters["featureFlags/ideaMDP427"];
      if (inspectionKit) {
        await saveVideoInspectionInstructions(
          accountId,
          props.responsibilityId,
          props.checkpointId,
          { inspection_instructions: suggestedInstructions }
        );
      }
    }
    emit("refresh-checkpoint");
  } catch (err) {
    console.error("[CheckpointInspectionInstructions] persist video_to_components result:", err);
    notificationStore.error(
      err?.response?.data?.detail ??
        err?.response?.data?.message ??
        "Could not save inspection draft."
    );
  }
}

async function clearInlineVideoToComponentsDraftFromBackend() {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  if (!accountId || props.responsibilityId == null || props.checkpointId == null) {
    return false;
  }
  await changeCheckpointV2(accountId, props.responsibilityId, props.checkpointId, {
    proposed_video_microlearning_is_mismatch: false,
    proposed_instructions: null,
    proposed_quiz: null,
    proposed_inspection_protocol: null,
    microlearning_instructions: null,
  });
  return true;
}

const handleInlineFromVideoIqOpenReview = async ({ suggestedInstructions }) => {
  const validationStatus =
    suggestedInstructions?.validation?.status ??
    suggestedInstructions?.inspection_instructions?.validation?.status ??
    null;
  const statusUpper = typeof validationStatus === "string" ? validationStatus.toUpperCase() : "";

  if (ideaMDP452.value && (suggestedInstructions?.error || statusUpper === "FAIL")) {
    const withError = {
      ...suggestedInstructions,
      error:
        suggestedInstructions?.error ??
        suggestedInstructions?.validation?.reasoning ??
        suggestedInstructions?.inspection_instructions?.validation?.reasoning ??
        "Video validation failed.",
    };
    inlineFromVideoSuggestedInstructions.value = withError;
    await persistInlineVideoToComponentsResult(withError, "fail");
    return;
  }

  if (
    ideaMDP452.value &&
    isInlineVideoToComponentsPassSuccess(
      suggestedInstructions,
      inlineVideoToComponentsSelection.value
    )
  ) {
    inlineFromVideoSuggestedInstructions.value = suggestedInstructions;
    passToolbarMenuTabIndex.value = defaultPassToolbarTabForTestingType(
      inlineVideoToComponentsSelection.value?.testingType
    );
    await persistInlineVideoToComponentsResult(suggestedInstructions, "pass");
    return;
  }

  showInlineFromVideoIq.value = false;
  await nextTick();
  iqOverlayStore.openOverlay({
    prompt: null,
    type: "videoInstructions",
    useCustomLoader: true,
    videoUrl: proposedVideoUrl.value,
    checkpointId: props.checkpointId,
    responsibilityId: props.responsibilityId,
    checkpointPrefix: props.checkpointPrefix,
    checkpointCriteria: props.checkpointCriteria,
    responsibilityCode: props.responsibilityCode,
    responsibilityName: props.responsibilityName,
    responsibilityPurpose: props.responsibilityPurpose,
    checkpointInstructions: props.checkpointInstructions,
    videoToComponentsSelection: inlineVideoToComponentsSelection.value,
  });
  await nextTick();
  iqOverlayStore.setSuggestedInstructions(suggestedInstructions);
  emit("iq-suggest-click");
};

function tryHydrateInlineVideoToComponentsFromCheckpoint() {
  if (!ideaMDP452.value) return;
  if (!showProposedVideoMenuItem.value) return;

  const mismatch = isProposedVideoMicrolearningMismatch.value;
  const rawProp =
    props.checkpointInspection?.proposed_instructions ??
    props.checkpoint?.proposed_instructions ??
    null;
  const mlRaw = props.checkpoint?.microlearning_instructions ?? null;
  const proposedQuizRaw =
    props.checkpointInspection?.proposed_quiz ?? props.checkpoint?.proposed_quiz ?? null;
  const proposedProtocolRaw =
    props.checkpointInspection?.proposed_inspection_protocol ??
    props.checkpoint?.proposed_inspection_protocol ??
    null;

  const mlIsMeaningful =
    mlRaw != null &&
    mlRaw !== "" &&
    !(typeof mlRaw === "object" && !Array.isArray(mlRaw) && Object.keys(mlRaw).length === 0);

  const hasProposedQuizOrProtocol =
    (proposedQuizRaw != null && proposedQuizRaw !== "") ||
    (proposedProtocolRaw != null && proposedProtocolRaw !== "");

  if (
    !mismatch &&
    (rawProp == null || rawProp === "") &&
    !mlIsMeaningful &&
    !hasProposedQuizOrProtocol
  ) {
    inlineFromVideoSuggestedInstructions.value = null;
    showInlineFromVideoIq.value = false;
    passToolbarMenuTabIndex.value = 0;
    return;
  }

  if (iqChatStore.isGenerating || iqChatStore.isLoading) return;

  if (
    inlineVideoToComponentsSelection.value != null &&
    !inlineFromVideoSuggestedInstructions.value
  ) {
    return;
  }

  if (showInlineFromVideoIq.value && !inlineFromVideoSuggestedInstructions.value) return;

  if (inlineFromVideoSuggestedInstructions.value) return;

  if (mismatch && rawProp) {
    const parsed = safeParseJson(rawProp);
    const payload = typeof parsed === "object" && parsed != null ? parsed : {};
    const err =
      payload.error ??
      payload.validation?.reasoning ??
      (typeof parsed === "string" ? parsed : null) ??
      "Video validation failed.";
    const videoUrl =
      payload.video_url ??
      (extractVideoUrlFromIframeHtml(String(proposedVideoUrl.value || "")) || null);
    inlineFromVideoSuggestedInstructions.value = {
      steps: [],
      summary: null,
      error: err,
      video_url: videoUrl,
      validation: payload.validation ?? { status: "FAIL", reasoning: err },
    };
    showInlineFromVideoIq.value = true;
    return;
  }

  if (!mismatch && mlIsMeaningful && typeof mlRaw === "object") {
    const o = mlRaw;
    const statusRaw = o.validation?.status ?? o.inspection_instructions?.validation?.status ?? null;
    const statusUpper = typeof statusRaw === "string" ? statusRaw.toUpperCase() : "";
    const proposedQuizParsed = safeParseJson(
      props.checkpointInspection?.proposed_quiz ?? props.checkpoint?.proposed_quiz ?? null
    );
    const proposedProtocolParsed = safeParseJson(
      props.checkpointInspection?.proposed_inspection_protocol ??
        props.checkpoint?.proposed_inspection_protocol ??
        null
    );
    const hasRich =
      statusUpper === "PASS" ||
      suggestedInstructionsHasQuiz(o) ||
      suggestedInstructionsHasQuiz({ quiz: proposedQuizParsed }) ||
      suggestedInstructionsHasInspectionProtocol(proposedProtocolParsed) ||
      (Array.isArray(o.chapters) && o.chapters.length > 0);
    if (hasRich) {
      inlineFromVideoSuggestedInstructions.value = passToolbarInlinePayloadFromCheckpoint(
        o,
        proposedProtocolRaw,
        proposedQuizRaw
      );
      showInlineFromVideoIq.value = true;
      const hydrated = inlineFromVideoSuggestedInstructions.value;
      if (
        suggestedInstructionsHasQuiz(hydrated) &&
        !suggestedInstructionsHasInspectionProtocol(proposedProtocolParsed)
      ) {
        passToolbarMenuTabIndex.value = 1;
      } else if (
        suggestedInstructionsHasInspectionProtocol(proposedProtocolParsed) &&
        !suggestedInstructionsHasQuiz(hydrated)
      ) {
        passToolbarMenuTabIndex.value = 2;
      } else {
        passToolbarMenuTabIndex.value = 0;
      }
    }
    return;
  }

  if (!mismatch && hasProposedQuizOrProtocol) {
    const quizParsed = safeParseJson(proposedQuizRaw);
    const protocolParsed = safeParseJson(proposedProtocolRaw);
    const rebuilt = passToolbarInlinePayloadFromCheckpoint(
      {
        quiz: quizParsed,
        ...(protocolParsed ? { inspection_instructions: protocolParsed } : {}),
      },
      proposedProtocolRaw,
      proposedQuizRaw
    );
    if (
      rebuilt &&
      (suggestedInstructionsHasQuiz(rebuilt) || checkpointProposedInspectionProtocolRaw())
    ) {
      inlineFromVideoSuggestedInstructions.value = rebuilt;
      showInlineFromVideoIq.value = true;
      if (suggestedInstructionsHasQuiz(rebuilt) && !checkpointProposedInspectionProtocolRaw()) {
        passToolbarMenuTabIndex.value = 1;
      } else if (
        checkpointProposedInspectionProtocolRaw() &&
        !suggestedInstructionsHasQuiz(rebuilt)
      ) {
        passToolbarMenuTabIndex.value = 2;
      } else {
        passToolbarMenuTabIndex.value = 0;
      }
    }
  }
}

watch(
  () => [
    ideaMDP452.value,
    showProposedVideoMenuItem.value,
    props.checkpointId,
    props.checkpoint?.updated_on,
    isProposedVideoMicrolearningMismatch.value,
    props.checkpointInspection?.proposed_instructions ?? props.checkpoint?.proposed_instructions,
    props.checkpoint?.microlearning_instructions,
    props.checkpointInspection?.proposed_quiz ?? props.checkpoint?.proposed_quiz,
    props.checkpointInspection?.proposed_inspection_protocol ??
      props.checkpoint?.proposed_inspection_protocol,
    props.checkpointInspection?.proposed_quiz_is_autopass ??
      props.checkpoint?.proposed_quiz_is_autopass,
  ],
  () => {
    void nextTick(() => tryHydrateInlineVideoToComponentsFromCheckpoint());
  },
  { immediate: true }
);

const handleInlineFromVideoIqDismiss = () => {
  showInlineFromVideoIq.value = false;
  inlineFromVideoSuggestedInstructions.value = null;
  inlineVideoToComponentsSelection.value = null;
  passToolbarMenuTabIndex.value = 0;
  iqChatStore.clearChat();
};

function applyProposeChangesOption(payload) {
  const option = payload?.option;
  switch (option) {
    case "copy_active":
      emit("propose-changes", { option });
      break;
    case "new_video":
      if (ideaMDP452.value) {
        showGenerateVideoMicrolearningDialog.value = true;
      } else {
        handleMicrolearningUploadClick();
      }
      break;
    case "new_article":
      emit("propose-click");
      break;
    default:
      break;
  }
}

defineExpose({ applyProposeChangesOption });

const onGenerateVideoMicrolearningDialogInput = (open) => {
  showGenerateVideoMicrolearningDialog.value = open;
};

const handleGenerateVideoMicrolearningDialogSubmit = (payload) => {
  showGenerateVideoMicrolearningDialog.value = false;
  const selection = {
    videoType: payload.videoType,
    testingType: payload.testingType,
  };
  if (ideaMDP452.value) {
    const suffix = buildGenerateVideoMicrolearningPromptSuffix(payload);
    inlineFromVideoIqUserPrompt.value = buildFromVideoIqUserPrompt(suffix);
    inlineFromVideoSuggestedInstructions.value = null;
    inlineVideoToComponentsSelection.value = selection;
    passToolbarMenuTabIndex.value = defaultPassToolbarTabForTestingType(payload.testingType);
    showInlineFromVideoIq.value = true;
    return;
  }
  pendingVideoToComponentsSelection.value = selection;
  openIqVideoSuggestOverlay(buildGenerateVideoMicrolearningPromptSuffix(payload));
};

const handleSuggestInstructions = () => {
  if (ideaMDP452.value) {
    showGenerateVideoMicrolearningDialog.value = true;
    return;
  }
  pendingVideoToComponentsSelection.value = null;
  openIqVideoSuggestOverlay();
};

const handleMicrolearningUploadClick = () => {
  if (props.checkpointId) {
    localStorage.setItem("selectedChecklist", JSON.stringify(props.checkpointId));
  }
  showUploadDialog.value = true;
  emit("microlearning-upload-click");
};

const handleRemoveActive = async () => {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  if (!accountId || props.responsibilityId == null || props.checkpointId == null) {
    console.error("[CheckpointInspectionInstructions] Missing IDs for remove active");
    return;
  }
  try {
    isRemoveActiveLoading.value = true;
    await removeActiveInspectionKit(accountId, props.responsibilityId, props.checkpointId);
    if (props.microlearningVideoId) {
      await deleteProposedVideo(accountId, props.checkpointId);
    }
    emit("refresh-checkpoint");
    emit("remove-active");
  } catch (err) {
    console.error("[CheckpointInspectionInstructions] Error removing active inspection kit:", err);
  } finally {
    isRemoveActiveLoading.value = false;
  }
};

const handleActiveThreeDotsClick = () => {
  emit("active-three-dots-click");
};

const handleActiveStepClick = () => {
  emit("active-step-click");
};

const handlePublishClick = () => {
  showPublishDialog.value = true;
};

const handlePublishConfirm = async () => {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  if (!accountId || props.responsibilityId == null || props.checkpointId == null) {
    console.error("[CheckpointInspectionInstructions] Missing IDs for publish");
    showPublishDialog.value = false;
    return;
  }
  try {
    isPublishLoading.value = true;
    showPublishDialog.value = false;
    await publishProposedInspectionKit(accountId, props.responsibilityId, props.checkpointId);
    emit("refresh-checkpoint");
    emit("publish");
  } catch (err) {
    console.error(
      "[CheckpointInspectionInstructions] Error publishing proposed inspection kit:",
      err
    );
  } finally {
    isPublishLoading.value = false;
  }
};

const handleDiscard = async () => {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  if (!accountId || props.responsibilityId == null || props.checkpointId == null) {
    console.error("[CheckpointInspectionInstructions] Missing IDs for discard (update checkpoint)");
    notificationStore.error("Missing account or checkpoint information.");
    return false;
  }
  try {
    isDiscardLoading.value = true;
    proxy?.$loader?.setAppLoading(true);
    clearInspectionStreamSimulation();
    await clearInlineVideoToComponentsDraftFromBackend();
    await discardProposedInspectionKit(accountId, props.responsibilityId, props.checkpointId);
    if (props.microlearningVideoId) {
      await deleteProposedVideo(accountId, props.checkpointId);
    }
    emit("refresh-checkpoint");
    emit("discard");
    return true;
  } catch (err) {
    console.error(
      "[CheckpointInspectionInstructions] Error discarding proposed instructions:",
      err
    );
    notificationStore.error(
      err?.response?.data?.detail ??
        err?.response?.data?.message ??
        "Could not remove microlearning."
    );
    return false;
  } finally {
    isDiscardLoading.value = false;
    proxy?.$loader?.setAppLoading(false);
  }
};

function resetInlineFailUiState() {
  inlineFromVideoSuggestedInstructions.value = null;
  showInlineFromVideoIq.value = false;
  inlineVideoToComponentsSelection.value = null;
  passToolbarMenuTabIndex.value = 0;
  iqChatStore.clearChat();
}

function handleInlineFailReplace() {
  pendingInlineReplaceReset.value = true;
  handleMicrolearningUploadClick();
}

const showDeleteMicrolearningVideoDialog = ref(false);

function openDeleteMicrolearningVideoDialog() {
  if (isDiscardLoading.value) {
    return;
  }
  showDeleteMicrolearningVideoDialog.value = true;
}

async function handleDeleteMicrolearningVideoDialogConfirm() {
  try {
    await handleInlineFailDelete();
  } finally {
    showDeleteMicrolearningVideoDialog.value = false;
  }
}

async function handleInlineFailDelete() {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  if (accountId != null && props.checkpointId != null) {
    try {
      await deleteProposedVideo(accountId, props.checkpointId);
    } catch (err) {
      console.error("[CheckpointInspectionInstructions] Error deleting proposed video:", err);
      notificationStore.error(
        err?.response?.data?.detail ??
          err?.response?.data?.message ??
          "Could not delete proposed video."
      );
      return;
    }
  }
  const ok = await handleDiscard();
  if (ok) {
    handleInlineFromVideoIqDismiss();
  }
}

const handleVideoUrlReady = async (payload) => {
  showUploadDialog.value = false;

  const key = inspectionStreamWebhookKey();
  if (key) cancelSimulatedCloudflareStream(key);
  clearInspectionStreamRevealTimer();

  pendingPostProposeStreamReveal.value = true;

  const signedUrl = payload?.signedUrl ?? null;

  if (signedUrl) {
    const uid = extractCloudflareStreamVideoUidFromEmbeds(signedUrl);
    if (uid) {
      await pollCloudflareStreamStatus({
        uid,
        pollMs: 1000,
        maxMs: 10 * 60 * 1000,
        fetcher: async (id) => {
          const res = await getCloudflareVideoStatus(id);
          return res?.data ?? null;
        },
      });
    }
  }

  pendingInspectionStreamReveal.value = false;
  pendingPostProposeStreamReveal.value = false;

  if (isPlayableSignedVideoUrl(signedUrl)) {
    localProposedMicrolearningOverride.value = signedUrl;
  } else {
    localProposedMicrolearningOverride.value = null;
  }

  emit("video-url-ready", payload);
};

const handleVideoUploaded = async (payload) => {
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  const checkpointData = payload?.checkpointData || payload;
  const videoId = checkpointData?.id ?? checkpointData?.video_id ?? checkpointData?.videoId;
  const shouldResetInlineDraft = pendingInlineReplaceReset.value;

  if (shouldResetInlineDraft) {
    try {
      await clearInlineVideoToComponentsDraftFromBackend();
      resetInlineFailUiState();
    } catch (err) {
      console.error(
        "[CheckpointInspectionInstructions] Error resetting draft after replace upload:",
        err
      );
      notificationStore.error(
        err?.response?.data?.detail ??
          err?.response?.data?.message ??
          "Could not reset previous generated content."
      );
    } finally {
      pendingInlineReplaceReset.value = false;
    }
  }

  if (ideaMDP452.value && inlineFromVideoSuggestedInstructions.value) {
    resetInlineFailUiState();
  }

  // Hide the upload loader immediately and reveal the processing state with the notification;
  // readiness is now driven by the cloudflare-video-status endpoint.
  showUploadDialog.value = false;
  scheduleInspectionStreamSimulationFromUpload();
  notificationStore.info(VIDEO_POST_UPLOAD_TOAST_TITLE, VIDEO_POST_UPLOAD_TOAST_MESSAGE, 8000);

  if (
    !accountId ||
    props.responsibilityId == null ||
    props.checkpointId == null ||
    videoId == null
  ) {
    emit("refresh-checkpoint");
    showUploadDialog.value = false;
    emit("video-uploaded", payload);
    return;
  }

  emit("video-uploaded", payload);
};
</script>

<style scoped>
.ex-checkpoint-inspection-instructions {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.ex-checkpoint-inspection-instructions__content {
  padding: 0px 0px 0px 0px;
}

.ex-checkpoint-inspection-instructions__article-microlearning {
  margin-top: 8px;
}

.ex-checkpoint-inspection-instructions__article-quiz {
  margin-bottom: 12px;
}

.ex-checkpoint-inspection-instructions__article-inspection {
  margin-bottom: 12px;
}

.ex-checkpoint-inspection-instructions__microlearning-view-container {
  margin-top: 16px;
}
.ex-checkpoint-inspection-instructions__microlearning-view {
  margin-top: 24px;
}

.ex-checkpoint-inspection-instructions__proposed-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}

.ex-checkpoint-inspection-instructions__proposed-label {
  position: relative;
}

.ex-checkpoint-inspection-instructions__actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ex-checkpoint-inspection-instructions__btn {
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition:
    background-color 0.2s,
    border-color 0.2s,
    color 0.2s;
}

.ex-checkpoint-inspection-instructions__btn--publish {
  background-color: #f2f4f7;
  color: #98a2b3;
  border: 1px solid #e4e7ec;
}

.ex-checkpoint-inspection-instructions__btn--publish:not(:disabled):hover {
  background-color: #e4e7ec;
  color: #667085;
}

.ex-checkpoint-inspection-instructions__btn--publish:disabled {
  cursor: not-allowed;
  opacity: 0.8;
}

.ex-checkpoint-inspection-instructions__btn--discard {
  background-color: #fff;
  color: #d92d20;
  border: 1px solid #d92d20;
}

.ex-checkpoint-inspection-instructions__btn--discard:hover {
  background-color: #fef3f2;
}

.ex-checkpoint-inspection-instructions__separator {
  height: 2px;
  background-color: #e4e7ec;
  margin-top: 8px;
  flex-shrink: 0;
}

.ex-checkpoint-inspection-instructions__stream-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  background: transparent;
}

.ex-checkpoint-inspection-instructions__stream-loading--banner {
  min-height: 280px;
  margin-top: 8px;
  padding: 24px 16px;
}

/* MDP-452: short loader while encoding (no tall empty sheet) */
.ex-checkpoint-inspection-instructions__stream-loading--banner-mdp452-compact {
  min-height: 100px;
  margin-top: 0;
  padding: 12px 12px 16px;
  gap: 8px;
}

.ex-checkpoint-inspection-instructions__stream-loading--in-player {
  position: absolute;
  inset: 0;
  z-index: 2;
}

.ex-checkpoint-inspection-instructions__video-bar {
  padding-bottom: 20px;
}

/* Full-screen video area */
.ex-checkpoint-inspection-instructions__content--with-video {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.ex-checkpoint-inspection-instructions__video-wrap {
  flex: 1;
  min-height: 360px;
  border-radius: 8px;
  overflow: hidden;
  background-color: #000;
  position: relative;
}

.ex-checkpoint-inspection-instructions__video-wrap--loading {
  background-color: transparent;
}

/* MDP-452: proposed video area — small strip instead of 360px black block while stream loads */
.ex-checkpoint-inspection-instructions__video-wrap--mdp452-stream-compact {
  min-height: 120px;
  max-height: 200px;
  flex: 0 0 auto;
  background-color: #f9fafb;
  border: 1px solid #e4e7ec;
}

.ex-checkpoint-inspection-instructions__stream-loading--mdp452-in-player-compact {
  gap: 8px;
  padding: 8px 8px 12px;
  box-sizing: border-box;
}

.ex-checkpoint-inspection-instructions__stream-loading--mdp452-in-player-compact
  .ex-checkpoint-inspection-instructions__video-processing-loader-wrap {
  width: 36px;
  height: 36px;
}

.ex-checkpoint-inspection-instructions__stream-loading--mdp452-in-player-compact
  .ex-checkpoint-inspection-instructions__video-processing-loader {
  width: 36px;
  height: 36px;
}

.ex-checkpoint-inspection-instructions__stream-loading--mdp452-in-player-compact
  .ex-checkpoint-inspection-instructions__video-processing-text {
  font-size: 14px;
  line-height: 20px;
}

.ex-checkpoint-inspection-instructions__video {
  width: 100%;
  height: 100%;
  min-height: 360px;
  object-fit: contain;
  display: block;
}

.ex-checkpoint-inspection-instructions__video--hidden {
  visibility: hidden;
}

.ex-checkpoint-inspection-instructions__video-iframe {
  width: 100%;
  min-height: 360px;
  display: block;
}

.ex-checkpoint-inspection-instructions__video-iframe :deep(iframe) {
  width: 100%;
  height: 100%;
  min-height: 360px;
  border: none;
}

.ex-checkpoint-inspection-instructions__video-processing-loader-wrap {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ex-checkpoint-inspection-instructions__video-processing-loader {
  width: 48px;
  height: 48px;
  display: block;
  flex-shrink: 0;
}

.ex-checkpoint-inspection-instructions__video-processing-text {
  margin: 0;
  font-family: Inter, sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  color: #344054;
  text-align: center;
}

.ex-checkpoint-inspection-instructions__video-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 360px;
  background-color: #f9fafb;
  padding: 24px;
}

.ex-checkpoint-inspection-instructions__video-placeholder p {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: #667085;
}

/* idea-MDP-452: inline mini IQ replaces Source Video bar + player */
.ex-checkpoint-inspection-instructions__inline-mini-iq {
  width: 100%;
}

.ex-checkpoint-inspection-instructions__inline-fail-panel {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.ex-checkpoint-inspection-instructions__inline-fail-actions {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.ex-checkpoint-inspection-instructions__inline-fail-regenerate :deep(.add-ask-iq-button) {
  height: 32px !important;
  min-width: 0;
  min-height: 32px;
  padding: 4px 10px 4px 8px !important;
  gap: 6px !important;
}

.ex-checkpoint-inspection-instructions__inline-fail-regenerate :deep(.add-ask-label) {
  font-size: 14px;
  line-height: 20px;
  white-space: nowrap;
}

/* idea-MDP-452: validation PASS — HorizontalMenu + CheckpointMicrolearningIcons + Delete + menu */
.ex-checkpoint-inspection-instructions__inline-pass-toolbar {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 0 0 12px;
  margin-bottom: 4px;
  box-sizing: border-box;
}

.ex-inline-pass-toolbar__main {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 16px;
  flex: 1 1 auto;
  min-width: 0;
}

.ex-inline-pass-toolbar__horizontal {
  flex: 1 1 auto;
  min-width: 0;
}

.ex-inline-pass-toolbar__horizontal :deep(.horizontal-tab .v-tabs-slider-wrapper) {
  color: #d6bbfb;
  height: 3px !important;
}

.ex-inline-pass-toolbar__horizontal :deep(.horizontal-tab .menu-tabs.v-tab.v-tab--active) {
  color: #7f56d9 !important;
}

.ex-inline-pass-toolbar__icons {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  padding-bottom: 2px;
}

.ex-inline-pass-toolbar__icon-slot {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 6px;
  border: 1px solid #eaecf0;
  background: #f9fafb;
  box-sizing: border-box;
}

.ex-inline-pass-toolbar__icon-slot--active {
  border-color: #7f56d9;
  background: #f4ebff;
}

.ex-inline-pass-toolbar__microlearning-icons {
  display: inline-flex;
  flex-shrink: 0;
}

.ex-inline-pass-toolbar__microlearning-icons :deep(svg) {
  display: block;
}

.ex-inline-pass-toolbar__actions {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  padding-bottom: 2px;
}

.ex-inline-pass-toolbar__menu {
  flex-shrink: 0;
  line-height: 0;
}

.ex-inline-pass-toolbar__menu :deep(.ex-menu-icon) {
  margin: 0;
  padding: 8px;
  box-shadow: none !important;
  border-radius: 8px;
}

.ex-inline-pass-toolbar__menu :deep(.ex-menu-icon:hover) {
  margin: 0;
  padding: 8px;
  box-shadow: none !important;
  background-color: rgba(16, 24, 40, 0.05) !important;
}

.ex-inline-pass-toolbar__menu :deep(.ex-menu-icon svg path) {
  stroke: #667085;
}

.ex-inline-pass-toolbar__body--full-row {
  flex: 1 1 100%;
  width: 100%;
  padding-top: 12px;
  box-sizing: border-box;
}

.ex-inline-pass-toolbar__quiz {
  width: 100%;
}

.ex-inline-pass-toolbar__protocol {
  width: 100%;
}

.ex-inline-pass-toolbar__protocol-empty {
  margin: 0;
  padding: 16px 0;
  color: #667085;
  font-size: 14px;
  line-height: 20px;
}
::v-deep .checkpoint-microlearning-icons {
  margin-top: 8px !important;
}

.timestamp-video-modal__video-container {
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  background: #000;
}

.timestamp-video-modal__video-frame {
  display: block;
  width: 100%;
  height: 450px;
  border: none;
  border-radius: 8px;
}
</style>
